(() => {
  const SELECTOR = 'mg-simple-personalised-product';

  const visible = (element) => {
    if (!element || element.hidden || element.disabled) return false;
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return element.getClientRects().length > 0;
  };

  const normalise = (value) => String(value || '').trim().toLowerCase();

  class MGSimplePersonalisedProduct extends HTMLElement {
    constructor() {
      super();
      this.state = {
        hand: '',
        size: '',
        quantity: 1,
        quantityTouched: false,
        giftReviewed: false,
        zeptoComplete: false,
        zeptoTouched: false,
        zeptoTextTouched: false,
        zeptoColourTouched: false,
        note: false,
        currentStep: 1,
        variant: null
      };

      this.variants = [];
      this.handIndex = Number(this.dataset.handOptionIndex || -1);
      this.sizeIndex = Number(this.dataset.sizeOptionIndex || -1);
      this.twoDiscount = Number(this.dataset.twoDiscount || 15);
      this.threeDiscount = Number(this.dataset.threeDiscount || 20);
      this.currency = this.dataset.currency || 'AUD';
      this.moneyFormat = this.dataset.moneyFormat || ('$' + '{' + '{amount}' + '}');
      this.zeptoDebounce = 0;
      this.submitting = false;
      this.allowNativeSubmit = false;
      this.formSubmitHandler = null;
      this.zeptoCaptureHandler = null;
      this.restoringConfiguration = false;
      this.saveTimer = 0;
      this.restoreTimers = [];
      this.zeptoInitTimers = [];
      this.shopifySectionLoadHandler = null;
      this.stampedBadgeObserver = null;
      this.galleryMediaQuery = null;
      this.galleryMediaQueryHandler = null;
      this.galleryScrollHandler = null;
      this.galleryScrollTimer = 0;
      this.galleryPointer = null;
      this.mobileDockObserver = null;
      this.activeOverlay = null;
      this.overlayInertTargets = [];
      this.bodyLockState = null;
      this.hasRestoredZeptoValues = false;
      this.cartSectionsFresh = false;
      this.zeptoLastColourLabel = '';
      this.zeptoConfirmedColourGroups = new Set();
      this.zeptoColourLabels = new Map();
      this.storageDays = Number(this.dataset.storageDays || 7);
      this.storageKey = `mg-builder-v35-16-10:${this.dataset.productId || location.pathname}`;
      this.userSelectedMedia = false;
      this.enabledSteps = String(this.dataset.enabledSteps || '1,2,3,4,5')
        .split(',')
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value >= 1 && value <= 5);
      this.itemSingular = String(this.dataset.itemSingular || 'item');
      this.itemPlural = String(this.dataset.itemPlural || 'items');
      this.requirePersonalisationColour = this.dataset.requirePersonalisationColour !== 'false';
      this.singlePersonalisationCopy = String(
        this.dataset.singlePersonalisationCopy || 'The selected personalisation will be applied to this {item}.'
      );
      this.multiPersonalisationCopy = String(
        this.dataset.multiPersonalisationCopy || 'All {quantity} {items} will receive the same personalisation.'
      );
      this.copy = {
        ready: this.dataset.copyReady || 'Ready to order',
        continue: this.dataset.copyContinue || 'Continue personalising',
        statusHand: this.dataset.copyStatusHand || 'Choose the required product option.',
        statusSize: this.dataset.copyStatusSize || 'Choose the size.',
        statusUnavailable: this.dataset.copyStatusUnavailable || 'This product option is unavailable.',
        statusPersonalisation: this.dataset.copyStatusPersonalisation || 'Complete the required personalisation before adding the order.',
        statusQuantity: this.dataset.copyStatusQuantity || 'Review the quantity options before ordering.',
        statusGift: this.dataset.copyStatusGift || 'Review the optional gift choices before ordering.',
        statusReview: this.dataset.copyStatusReview || 'Review the details below before adding the personalised order.',
        addToCart: this.dataset.copyAddToCart || 'Add personalised order to cart'
      };
    }

    connectedCallback() {
      if (this.dataset.initialised === 'true') return;
      this.dataset.initialised = 'true';

      this.form = this.querySelector('[data-product-form]');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.quantityInput = this.querySelector('[data-quantity-input]');
      this.atc = this.querySelector('[data-add-to-cart]');
      this.nativeSubmit = this.querySelector('[data-native-submit]');
      this.status = this.querySelector('[data-status]');
      this.zeptoContainer = this.querySelector('[data-zepto-container]');
      this.gallery = this.querySelector('[data-product-gallery]');

      // Zepto may preload a colour internally. A preload is not treated as a
      // customer confirmation until the shopper interacts with Zepto's own
      // colour control.
      this.dataset.zeptoColourConfirmed = 'false';

      try {
        const variantsNode = this.querySelector('[data-product-variants]');
        this.variants = variantsNode ? JSON.parse(variantsNode.textContent || '[]') : [];
      } catch (error) {
        console.error('[Mr Golf] Could not read product variants.', error);
      }

      const initialVariantId = Number(this.variantInput?.value || 0);
      this.state.variant = this.variants.find((variant) => Number(variant.id) === initialVariantId)
        || this.variants.find((variant) => variant.available)
        || this.variants[0]
        || null;

      this.bind();
      this.bindZeptoCapture();
      this.initialiseGallery();
      this.initialiseMobileDock();
      this.initialiseStamped();
      this.initialiseLazyStamped();
      this.initialiseAddons();
      this.restoreConfiguration();
      if (this.isStepEnabled(3)) {
        this.observeZepto();
        this.styleZeptoColourControls();
      }
      this.resolveVariant();
      this.updateSizeAvailability();
      this.update();
      this.openStep(Number(this.dataset.initialStep || this.firstEnabledStep()), false);
      [450, 1400, 2800].forEach((delay) => {
        const timer = window.setTimeout(() => this.restoreZeptoValues(), delay);
        this.restoreTimers.push(timer);
      });
      this.track('builder_viewed', { enabled_steps: this.enabledSteps.join(',') });
    }

    bind() {
      this.addEventListener('click', (event) => {
        const primaryAdd = event.target.closest('[data-add-to-cart]');
        if (primaryAdd && this.contains(primaryAdd)) {
          event.preventDefault();
          event.stopPropagation();

          if (this.submitting) return;

          this.checkZepto();
          if (!this.validateBeforeSubmit()) return;

          this.submitSelectedOrder();
          return;
        }

        if (this.zeptoContainer?.contains(event.target)) {
          this.confirmZeptoColourInteraction(event.target, 'click', event.isTrusted);
          this.noteZeptoInteraction(event.target, 'click', event.isTrusted);
        }

        const galleryThumb = event.target.closest('[data-gallery-thumb]');
        if (galleryThumb && this.contains(galleryThumb)) {
          this.userSelectedMedia = true;
          this.activateMedia(galleryThumb.dataset.galleryThumb || '', true);
          return;
        }

        const minus = event.target.closest('[data-quantity-minus]');
        if (minus && this.contains(minus)) {
          this.clearStepError(4);
          this.state.quantityTouched = true;
          this.state.quantity = Math.max(this.customQuantityMin(), Number(this.state.quantity || this.customQuantityMin()) - 1);
          this.update();
          return;
        }

        const plus = event.target.closest('[data-quantity-plus]');
        if (plus && this.contains(plus)) {
          this.clearStepError(4);
          this.state.quantityTouched = true;
          this.state.quantity = Math.min(9999, Math.max(this.customQuantityMin(), Number(this.state.quantity || this.customQuantityMin()) + 1));
          this.update();
          return;
        }

        const quantityContinue = event.target.closest('[data-quantity-continue]');
        if (quantityContinue && this.contains(quantityContinue)) {
          this.clearStepError(4);

          const customInput = this.querySelector('[data-quantity-number]');
          const customMin = this.customQuantityMin();
          const enteredQuantity = Number(customInput?.value || this.state.quantity || customMin);

          this.state.quantityTouched = true;
          this.state.quantity = Math.min(9999, Math.max(customMin, enteredQuantity));

          if (customInput) customInput.value = String(this.state.quantity);
          if (this.quantityInput) this.quantityInput.value = String(this.state.quantity);

          this.track('custom_quantity_confirmed', {
            quantity: this.state.quantity,
            saving_cents: Math.max(
              0,
              this.baseVariantPrice() * this.state.quantity - this.quantityPrice(this.state.quantity)
            )
          });

          this.update();

          const nextStep = this.enabledSteps.find((candidate) => candidate > 4);
          if (nextStep) {
            this.openStep(nextStep);
          } else {
            const quantityStep = this.querySelector('[data-step="4"]');
            quantityStep?.classList.remove('is-open');
            quantityStep?.querySelector('[data-step-header]')?.setAttribute('aria-expanded', 'false');

            window.setTimeout(() => {
              this.querySelector('[data-order-review]')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
              this.atc?.focus?.({ preventScroll: true });
            }, 120);
          }

          return;
        }

        const customQuantityCard = event.target.closest('[data-custom-quantity-card]');
        if (
          customQuantityCard
          && this.contains(customQuantityCard)
          && !event.target.closest('button, input')
        ) {
          this.clearStepError(4);
          this.state.quantityTouched = true;
          this.state.quantity = Math.max(this.customQuantityMin(), Number(this.state.quantity || this.customQuantityMin()));
          this.update();
          window.setTimeout(() => {
            const input = customQuantityCard.querySelector('[data-quantity-number]');
            input?.focus?.();
            input?.select?.();
          }, 60);
          return;
        }

        const hand = event.target.closest('[data-hand-value]');
        if (hand && this.contains(hand)) {
          this.clearStepError(1);
          this.state.hand = hand.dataset.handValue || '';
          this.state.size = '';
          this.track('hand_selected', { hand: this.state.hand });
          this.state.variant = null;
          this.updateSizeAvailability();
          this.update();
          if (!this.isStepEnabled(2)) this.resolveVariant();
          this.openStep(this.nextEnabledStep(1));
          return;
        }

        const size = event.target.closest('[data-size-value]');
        if (size && this.contains(size) && !size.disabled) {
          this.clearStepError(2);
          this.state.size = size.dataset.sizeValue || '';
          this.track('size_selected', { size: this.state.size });
          this.resolveVariant();
          this.update();
          this.openStep(this.nextEnabledStep(2));
          return;
        }

        const quantity = event.target.closest('[data-quantity-choice]');
        if (quantity && this.contains(quantity)) {
          this.clearStepError(4);
          this.state.quantityTouched = true;
          this.state.quantity = Number(quantity.dataset.quantityChoice || 1);

          this.track('quantity_selected', {
            quantity: this.state.quantity,
            selection_type: 'preset',
            saving_cents: Math.max(0, this.baseVariantPrice() * this.state.quantity - this.quantityPrice(this.state.quantity))
          });

          if (this.quantityInput) this.quantityInput.value = String(this.state.quantity);

          if (this.state.quantity > 1) {
            quantity.classList.remove('is-highlighted');
            void quantity.offsetWidth;
            quantity.classList.add('is-highlighted');
          }

          this.update();
          this.openStep(this.nextEnabledStep(4));
          return;
        }

        if (event.target.closest('[data-personalisation-continue]')) {
          this.checkZepto();
          if (!this.state.zeptoComplete) {
            this.showStepError(3, this.dataset.personalisationContinueError || 'Enter the personalisation text and choose a colour before continuing.', '[data-zepto-container]');
            this.setError(this.dataset.personalisationContinueError || 'Complete the personalisation before continuing.');
            return;
          }

          this.clearStepError(3);
          this.track('personalisation_continue_clicked', { summary: this.getZeptoSummary() });
          const nextStep = this.nextEnabledStep(3);
          this.openStep(nextStep);
          window.setTimeout(() => {
            this.querySelector(`[data-step="${nextStep}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }, 80);
          return;
        }

        if (event.target.closest('[data-scroll-to-product-image]')) {
          const gallery = this.querySelector('[data-gallery-main]');
          if (gallery) {
            gallery.setAttribute('tabindex', '-1');
            gallery.scrollIntoView({ behavior: 'smooth', block: 'center' });
            window.setTimeout(() => gallery.focus({ preventScroll: true }), 450);
          }
          this.track('live_preview_requested');
          return;
        }

        if (event.target.closest('[data-open-hand]')) {
          event.preventDefault();
          event.stopPropagation();
          this.openDialog('[data-hand-dialog]');
          this.track('hand_help_opened');
          return;
        }

        const header = event.target.closest('[data-step-header]');
        if (header && this.contains(header)) {
          this.openStep(Number(header.dataset.stepHeader || 1));
          return;
        }

        const addonChoice = event.target.closest('[data-simple-addon-choice]');
        if (addonChoice && this.contains(addonChoice) && !addonChoice.disabled) {
          const card = addonChoice.closest('[data-simple-addon-card]');
          this.applyAddonChoice(card, addonChoice);
          card.querySelector('.mg-simple-addon__swatches')?.classList.remove('is-required');
          this.update();
          return;
        }

        const addonToggle = event.target.closest('[data-simple-addon-toggle]');
        if (addonToggle && this.contains(addonToggle) && !addonToggle.disabled) {
          const card = addonToggle.closest('[data-simple-addon-card]');
          const selecting = card.dataset.addonSelected !== 'true';

          if (selecting && !card.dataset.addonVariantId) {
            const status = card.querySelector('[data-addon-status]');
            if (status) status.textContent = 'Choose a colour first.';
            card.querySelector('.mg-simple-addon__swatches')?.classList.add('is-required');
            window.setTimeout(() => card.querySelector('.mg-simple-addon__swatches')?.classList.remove('is-required'), 900);
            return;
          }

          this.state.giftReviewed = true;
          this.clearStepError(5);
          this.setAddonSelected(card, selecting);
          this.track(selecting ? 'gift_addon_added' : 'gift_addon_removed', { colour: card.dataset.addonColour || '' });
          this.update();
          return;
        }

        const giftSkip = event.target.closest('[data-gift-skip]');
        if (giftSkip && this.contains(giftSkip)) {
          const hasSelectedExtras = this.selectedAddonCards().length > 0 || Boolean(this.state.note);

          this.state.giftReviewed = true;
          this.clearStepError(5);

          if (!hasSelectedExtras) {
            // True skip: clear any partially chosen extras/colours and continue.
            this.state.note = false;

            const noteInput = this.querySelector('[data-note-input]');
            if (noteInput) noteInput.value = '';

            this.querySelectorAll('[data-simple-addon-card]').forEach((card) => {
              this.setAddonSelected(card, false);
              this.clearAddonChoice(card);
            });

            this.track('gift_extras_skipped');
          } else {
            // Extras have been deliberately selected. Preserve them and simply
            // collapse the step so the customer can confirm the order below.
            this.track('gift_extras_confirmed', {
              addons: this.selectedAddonCards().length,
              note: Boolean(this.state.note)
            });
          }

          const giftStep = this.querySelector('[data-step="5"]');
          giftStep?.classList.remove('is-open');
          giftStep?.querySelector('[data-step-header]')?.setAttribute('aria-expanded', 'false');

          this.update();

          window.setTimeout(() => {
            const review = this.querySelector('[data-order-review]');
            review?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            this.atc?.focus?.({ preventScroll: true });
          }, 120);
          return;
        }

        const noteToggle = event.target.closest('[data-note-toggle]');
        if (noteToggle && this.contains(noteToggle)) {
          this.state.giftReviewed = true;
          this.clearStepError(5);
          this.state.note = !this.state.note;
          this.track(this.state.note ? 'gift_note_added' : 'gift_note_removed');
          this.update();
          if (this.state.note) window.setTimeout(() => this.querySelector('[data-note-input]')?.focus(), 120);
          return;
        }

        if (event.target.closest('[data-open-size]')) {
          this.openDialog('[data-size-dialog]');
          return;
        }

        if (event.target.closest('[data-close-dialog]')) {
          const dialog = event.target.closest('dialog');
          const overlay = event.target.closest('[data-modal-overlay]');
          if (dialog) dialog.close();
          if (overlay) this.closeOverlay(overlay);
          return;
        }

        const overlay = event.target.closest('[data-modal-overlay]');
        if (overlay && event.target === overlay) {
          this.closeOverlay(overlay);
          return;
        }

        if (event.target.closest('[data-clear-saved]')) {
          this.clearSavedConfiguration(true);
          return;
        }


        if (event.target.closest('[data-mobile-action]')) {
          const guidedStep = this.nextGuidedStep();
          if (guidedStep) {
            this.openStep(guidedStep);
            this.querySelector(`[data-step="${guidedStep}"]`)?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          } else if (this.atc) {
            this.atc.click();
          }
        }
      });

      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-note-input]')) {
          this.updateNoteCount();
          this.syncGiftNoteProperties();
          this.update();
        }

        if (event.target.matches('[data-quantity-number]')) {
          const cleaned = String(event.target.value || '').replace(/\D/g, '').slice(0, 4);
          event.target.value = cleaned;
          if (!cleaned) return;

          this.clearStepError(4);
          this.state.quantityTouched = true;
          this.state.quantity = Math.min(9999, Math.max(this.customQuantityMin(), Number(cleaned)));
          this.update();
        }

        if (this.isZeptoEventTarget(event.target)) {
          if (!this.state.zeptoTouched) this.track('personalisation_started');
          this.noteZeptoInteraction(event.target, 'input');
          this.scheduleZeptoCheck();
        }
      });

      this.addEventListener('keydown', (event) => {
        const header = event.target.closest?.('[data-step-header][role="button"]');
        if (
          header
          && event.target === header
          && (event.key === 'Enter' || event.key === ' ')
        ) {
          event.preventDefault();
          this.openStep(Number(header.dataset.stepHeader || 1));
          return;
        }

        if (this.activeOverlay && !this.activeOverlay.hidden && event.key === 'Tab') {
          const focusable = this.overlayFocusable(this.activeOverlay);
          if (!focusable.length) {
            event.preventDefault();
            this.activeOverlay.querySelector('[role="dialog"]')?.focus?.();
            return;
          }

          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          const active = document.activeElement;

          if (event.shiftKey && (active === first || !this.activeOverlay.contains(active))) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
          }
        }

        if (event.key === 'Escape') {
          const overlay = this.querySelector('[data-modal-overlay]:not([hidden])');
          if (overlay) {
            event.preventDefault();
            this.closeOverlay(overlay);
          }
        }
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-quantity-number]')) {
          const cleaned = String(event.target.value || '').replace(/\D/g, '').slice(0, 4);
          this.state.quantityTouched = true;
          this.state.quantity = Math.min(9999, Math.max(this.customQuantityMin(), Number(cleaned || this.customQuantityMin())));
          event.target.value = String(this.state.quantity);
          this.track('custom_quantity_entered', {
            quantity: this.state.quantity,
            saving_cents: Math.max(0, this.baseVariantPrice() * this.state.quantity - this.quantityPrice(this.state.quantity))
          });
          this.update();
        }

        if (this.isZeptoEventTarget(event.target)) {
          this.noteZeptoInteraction(event.target, 'change');
          this.scheduleZeptoCheck();
        }
      });


      if (this.form) {
        this.formSubmitHandler = (event) => {
          if (this.allowNativeSubmit) {
            this.allowNativeSubmit = false;
            return;
          }

          // Any implicit/external submit must return to the controlled builder
          // flow. This prevents Zepto/theme code from silently adding only the
          // main product when a gift-set add-on is selected.
          event.preventDefault();
          event.stopPropagation();

          this.checkZepto();
          if (!this.validateBeforeSubmit()) return;
          this.submitSelectedOrder();
        };

        this.form.addEventListener('submit', this.formSubmitHandler, true);
      }

      this.shopifySectionLoadHandler = (event) => {
        if (event.target?.contains(this) && this.isStepEnabled(3)) {
          this.observeZepto();
          this.styleZeptoColourControls();
        }
      };
      document.addEventListener('shopify:section:load', this.shopifySectionLoadHandler);
    }

    submitNativeMainProduct() {
      if (!this.form || !this.nativeSubmit) {
        this.setError('The product form could not be submitted. Please refresh and try again.');
        return;
      }

      // Final guard: the actual Zepto/form values sent to Shopify are uppercase,
      // not merely styled to look uppercase.
      this.uppercaseAllZeptoPersonalisationText(true);
      this.allowNativeSubmit = true;

      try {
        this.form.requestSubmit(this.nativeSubmit);
      } catch (error) {
        this.allowNativeSubmit = false;
        console.error('[Mr Golf] Native product submission failed.', error);
        this.setError('The product could not be added. Please refresh and try again.');
      }
    }

    track(name, details = {}) {
      const payload = {
        event: `mg_personalisation_${name}`,
        product_id: Number(this.dataset.productId || 0),
        product_url: this.dataset.productUrl || location.pathname,
        timestamp: new Date().toISOString(),
        ...details
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      this.dispatchEvent(new CustomEvent('mg:personalisation', { bubbles: true, detail: payload }));
    }

    captureZeptoValues() {
      const output = {};
      this.getZeptoControls().forEach((control, index) => {
        if (control.type === 'file') return;
        const key = control.name || control.id || `control-${index}`;
        if (control.type === 'checkbox' || control.type === 'radio') output[key] = Boolean(control.checked);
        else output[key] = String(control.value || '');
      });
      return output;
    }

    restoreZeptoValues() {
      if (!this.savedZeptoValues || !this.isStepEnabled(3)) return;
      let restored = 0;
      this.getZeptoControls().forEach((control, index) => {
        if (control.type === 'file') return;
        const key = control.name || control.id || `control-${index}`;
        if (!(key in this.savedZeptoValues)) return;
        const value = this.savedZeptoValues[key];
        if (control.type === 'checkbox' || control.type === 'radio') {
          control.checked = Boolean(value);
        } else {
          control.value = this.isZeptoTextControl(control)
            ? this.uppercaseValue(value)
            : String(value || '');
        }
        control.dispatchEvent(new Event('input', { bubbles: true }));
        control.dispatchEvent(new Event('change', { bubbles: true }));
        restored += 1;
      });
      if (restored) {
        this.state.zeptoTouched = true;

        const restoredControls = this.getZeptoControls();
        restoredControls.forEach((control) => {
          if (!this.controlHasValue(control, restoredControls)) return;
          if (this.isZeptoTextControl(control)) this.state.zeptoTextTouched = true;
        });

        // Do not infer zeptoColourTouched from restored values. The explicit
        // confirmation flag is restored separately from a V35.10 save only.
        this.dataset.zeptoColourConfirmed = this.state.zeptoColourTouched ? 'true' : 'false';
        this.scheduleZeptoCheck();
      }
    }

    restoreConfiguration() {
      try {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (!saved.expires || Date.now() > saved.expires) {
          localStorage.removeItem(this.storageKey);
          return;
        }
        this.restoringConfiguration = true;
        this.state.hand = saved.hand || '';
        this.state.size = saved.size || '';
        this.state.quantity = Math.max(1, Math.min(9999, Number(saved.quantity || 1)));
        this.state.quantityTouched = Boolean(saved.quantityTouched);
        this.state.giftReviewed = Boolean(saved.giftReviewed);
        this.state.note = Boolean(saved.note);
        this.zeptoConfirmedColourGroups = new Set(
          Array.isArray(saved.zeptoConfirmedColourGroups)
            ? saved.zeptoConfirmedColourGroups.map((value) => String(value))
            : []
        );
        this.zeptoColourLabels = new Map(
          Object.entries(saved.zeptoColourLabels || {}).map(([key, value]) => [
            String(key),
            this.formatZeptoColourLabel(value)
          ])
        );
        this.state.zeptoColourTouched = this.zeptoConfirmedColourGroups.size > 0;
        this.dataset.zeptoColourConfirmed = 'false';
        this.savedZeptoValues = saved.zepto || {};
        this.hasRestoredZeptoValues = Object.values(this.savedZeptoValues).some((value) => {
          return value === true || String(value || '').trim() !== '';
        });
        const note = this.querySelector('[data-note-input]');
        if (note && saved.noteText != null) note.value = saved.noteText;

        (saved.addons || []).forEach((savedAddon) => {
          const card = [...this.querySelectorAll('[data-simple-addon-card]')].find((candidate) => candidate.dataset.addonTitle === savedAddon.title);
          if (!card) return;
          const choice = [...card.querySelectorAll('[data-simple-addon-choice]')].find((candidate) => candidate.dataset.choiceLabel === savedAddon.colour);
          if (choice) this.applyAddonChoice(card, choice, false);
          this.setAddonSelected(card, Boolean(savedAddon.selected));
        });
        this.querySelector('[data-saved-configuration]')?.removeAttribute('hidden');
        this.restoringConfiguration = false;
        this.track('configuration_restored');
      } catch (error) {
        console.warn('[Mr Golf] Saved configuration could not be restored.', error);
        try { localStorage.removeItem(this.storageKey); } catch (storageError) {}
        this.restoringConfiguration = false;
      }
    }

    scheduleSaveConfiguration() {
      if (this.restoringConfiguration) return;
      window.clearTimeout(this.saveTimer);
      this.saveTimer = window.setTimeout(() => this.saveConfiguration(), 220);
    }

    saveConfiguration() {
      const meaningful = this.state.hand || this.state.size || this.state.zeptoTouched || this.state.quantityTouched || this.state.note || this.selectedAddonCards().length;
      if (!meaningful) return;
      try {
        const note = this.querySelector('[data-note-input]');
        const payload = {
          expires: Date.now() + this.storageDays * 86400000,
          hand: this.state.hand,
          size: this.state.size,
          quantity: this.state.quantity,
          quantityTouched: this.state.quantityTouched,
          giftReviewed: this.state.giftReviewed,
          note: this.state.note,
          noteText: note?.value || '',
          zeptoColourTouched: this.state.zeptoColourTouched,
          zeptoConfirmedColourGroups: [...this.zeptoConfirmedColourGroups],
          zeptoColourLabels: Object.fromEntries(this.zeptoColourLabels),
          zepto: this.captureZeptoValues(),
          addons: [...this.querySelectorAll('[data-simple-addon-card]')].map((card) => ({
            title: card.dataset.addonTitle || '',
            colour: card.dataset.addonColour || '',
            selected: card.dataset.addonSelected === 'true'
          }))
        };
        localStorage.setItem(this.storageKey, JSON.stringify(payload));
      } catch (error) {
        console.warn('[Mr Golf] Could not save configuration.', error);
      }
    }

    clearSavedConfiguration(reset = false) {
      try { localStorage.removeItem(this.storageKey); } catch (error) {
        console.warn('[Mr Golf] Saved configuration could not be cleared.', error);
      }
      this.querySelector('[data-saved-configuration]')?.setAttribute('hidden', '');
      if (reset) {
        this.state.hand = '';
        this.state.size = '';
        this.state.quantity = 1;
        this.state.quantityTouched = false;
        this.state.giftReviewed = false;
        this.state.note = false;
        this.state.zeptoComplete = false;
        this.state.zeptoTouched = false;
        this.state.zeptoTextTouched = false;
        this.state.zeptoColourTouched = false;
        this.zeptoLastColourLabel = '';
        this.zeptoConfirmedColourGroups.clear();
        this.zeptoColourLabels.clear();
        this.savedZeptoValues = {};
        this.hasRestoredZeptoValues = false;
        this.querySelectorAll('[data-simple-addon-card]').forEach((card) => {
          this.setAddonSelected(card, false);
          this.clearAddonChoice(card);
        });

        this.getZeptoControls().forEach((control) => {
          if (control.type === 'file') return;
          if (control.type === 'checkbox' || control.type === 'radio') {
            control.checked = false;
          } else {
            control.value = '';
          }
          control.dispatchEvent(new Event('input', { bubbles: true }));
          control.dispatchEvent(new Event('change', { bubbles: true }));
        });

        // Synthetic reset events must not be mistaken for customer input.
        // Personalisation colour remains unconfirmed until a real customer
        // interaction chooses one.
        this.state.zeptoComplete = false;
        this.state.zeptoTouched = false;
        this.state.zeptoTextTouched = false;
        this.state.zeptoColourTouched = false;
        this.dataset.zeptoColourConfirmed = 'false';

        this.resolveVariant();
        this.updateSizeAvailability();
        this.update();
        this.openStep(this.firstEnabledStep());

        const resetTimer = window.setTimeout(() => {
          this.styleZeptoColourControls();
          this.checkZepto();
        }, 120);
        this.zeptoInitTimers.push(resetTimer);

        this.track('configuration_cleared');
      }
    }

    initialiseLazyStamped() {
      const wrapper = this.querySelector('[data-lazy-stamped]');
      if (!wrapper) return;
      const load = () => {
        if (wrapper.dataset.loaded === 'true') return;
        const template = wrapper.querySelector('[data-stamped-template]');
        const placeholder = wrapper.querySelector('[data-stamped-placeholder]');
        if (!template) return;
        wrapper.appendChild(template.content.cloneNode(true));
        placeholder?.remove();
        template.remove();
        wrapper.dataset.loaded = 'true';
        window.setTimeout(() => {
          try { window.StampedFn?.reloadUGC?.(); } catch (error) { console.warn('[Mr Golf] Stamped refresh failed.', error); }
        }, 50);
      };
      if (!('IntersectionObserver' in window)) { load(); return; }
      this.stampedObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          load();
          this.stampedObserver?.disconnect();
        }
      }, { rootMargin: '500px 0px' });
      this.stampedObserver.observe(wrapper);
    }

    displayedTotal() {
      return this.quantityPrice(this.state.quantity) + this.addonDisplayTotal();
    }

    updateCartMetadata() {
      const summary = [this.state.hand, this.state.size, this.getZeptoSummary(), this.quantityLabel(this.state.quantity)].filter(Boolean).join(' · ');
      const quantitySaving = Math.max(0, this.baseVariantPrice() * this.state.quantity - this.quantityPrice(this.state.quantity));
      const edit = this.querySelector('[data-edit-url-property]');
      const config = this.querySelector('[data-configuration-property]');
      const saving = this.querySelector('[data-quantity-saving-property]');
      const same = this.querySelector('[data-same-personalisation-property]');
      const expectedTotal = this.querySelector('[data-expected-total-property]');
      if (edit) edit.value = `${location.pathname}?continue_personalisation=1#${this.id}`;
      if (config) config.value = summary;
      if (saving) saving.value = quantitySaving > 0 ? this.formatMoney(quantitySaving) : '';
      if (same) same.value = this.state.quantity > 1 ? `All ${this.state.quantity} ${this.itemPlural} use the same personalisation` : '';
      if (expectedTotal) expectedTotal.value = this.formatMoney(this.displayedTotal());
    }

    submitSelectedOrder() {
      if (this.submitting) return;

      const addOns = this.selectedAddonCards();
      this.track('add_to_cart_started', {
        quantity: this.state.quantity,
        total_cents: this.displayedTotal(),
        addons: addOns.length
      });

      if (addOns.length) {
        this.submitWithAddons();
      } else {
        this.submitNativeMainProduct();
      }
    }

    initialiseGallery() {
      this.galleryMediaQuery = window.matchMedia('(max-width: 720px)');
      this.galleryMediaQueryHandler = () => this.applyGalleryMode();
      this.galleryMediaQuery.addEventListener?.('change', this.galleryMediaQueryHandler);

      const track = this.querySelector('[data-gallery-main]');
      if (track) {
        this.galleryScrollHandler = () => {
          if (!this.galleryMediaQuery?.matches) return;
          window.clearTimeout(this.galleryScrollTimer);
          this.galleryScrollTimer = window.setTimeout(() => this.syncGalleryFromScroll(), 90);
        };
        track.addEventListener('scroll', this.galleryScrollHandler, { passive: true });

        track.addEventListener('pointerdown', (event) => {
          this.galleryPointer = { x: event.clientX, y: event.clientY, moved: false };
        }, { passive: true });

        track.addEventListener('pointermove', (event) => {
          if (!this.galleryPointer) return;
          if (Math.abs(event.clientX - this.galleryPointer.x) > 8 || Math.abs(event.clientY - this.galleryPointer.y) > 8) {
            this.galleryPointer.moved = true;
          }
        }, { passive: true });

        track.addEventListener('pointerup', (event) => {
          const pointer = this.galleryPointer;
          this.galleryPointer = null;
          if (!pointer || pointer.moved) return;

          const image = event.target.closest?.('img');
          if (image && this.galleryMediaQuery?.matches) {
            this.openGalleryZoom(image);
          }
        });
      }

      this.applyGalleryMode();
      const active = this.querySelector('[data-gallery-slide].is-active') || this.querySelector('[data-gallery-slide]');
      if (active) this.activateMedia(active.dataset.gallerySlide || '', false);
    }

    applyGalleryMode() {
      const mobile = Boolean(this.galleryMediaQuery?.matches);
      const slides = [...this.querySelectorAll('[data-gallery-slide]')];

      slides.forEach((slide) => {
        if (mobile) {
          slide.hidden = false;
          slide.setAttribute('aria-hidden', 'false');
        } else {
          const active = slide.classList.contains('is-active');
          slide.hidden = !active;
          slide.setAttribute('aria-hidden', String(!active));
        }
      });

      if (mobile) {
        const active = slides.find((slide) => slide.classList.contains('is-active')) || slides[0];
        window.setTimeout(() => active?.scrollIntoView?.({ block: 'nearest', inline: 'start' }), 0);
      }
    }

    syncGalleryFromScroll() {
      if (!this.galleryMediaQuery?.matches) return;
      const track = this.querySelector('[data-gallery-main]');
      const slides = [...this.querySelectorAll('[data-gallery-slide]')];
      if (!track || !slides.length) return;

      const trackRect = track.getBoundingClientRect();
      const center = trackRect.left + trackRect.width / 2;
      let closest = slides[0];
      let distance = Infinity;

      slides.forEach((slide) => {
        const rect = slide.getBoundingClientRect();
        const next = Math.abs((rect.left + rect.width / 2) - center);
        if (next < distance) {
          distance = next;
          closest = slide;
        }
      });

      const id = closest?.dataset.gallerySlide || '';
      if (!id) return;

      slides.forEach((slide) => slide.classList.toggle('is-active', slide === closest));
      this.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
        const active = String(button.dataset.galleryThumb) === String(id);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    activateMedia(id, markUser = false) {
      if (!id) return;
      if (markUser) this.userSelectedMedia = true;

      const mobile = Boolean(this.galleryMediaQuery?.matches);
      let activeSlide = null;

      this.querySelectorAll('[data-gallery-slide]').forEach((slide) => {
        const active = String(slide.dataset.gallerySlide) === String(id);
        if (active) activeSlide = slide;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', mobile ? 'false' : String(!active));
        slide.hidden = mobile ? false : !active;

        if (!active) slide.querySelectorAll('video').forEach((video) => video.pause?.());
      });

      let activeThumb = null;
      this.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
        const active = String(button.dataset.galleryThumb) === String(id);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        if (active) activeThumb = button;
      });

      if (mobile && activeSlide && markUser) {
        const track = this.querySelector('[data-gallery-main]');

        if (track) {
          const left = Math.max(0, activeSlide.offsetLeft - track.offsetLeft);
          track.scrollTo({
            left,
            behavior: 'smooth'
          });
        }

        const thumbs = this.querySelector('[data-gallery-thumbs]');
        if (thumbs && activeThumb) {
          const thumbLeft = activeThumb.offsetLeft;
          const thumbRight = thumbLeft + activeThumb.offsetWidth;
          const visibleLeft = thumbs.scrollLeft;
          const visibleRight = visibleLeft + thumbs.clientWidth;

          if (thumbLeft < visibleLeft || thumbRight > visibleRight) {
            thumbs.scrollTo({
              left: Math.max(0, thumbLeft - (thumbs.clientWidth - activeThumb.offsetWidth) / 2),
              behavior: 'smooth'
            });
          }
        }
      }
    }

    openGalleryZoom(image) {
      const overlay = this.querySelector('[data-gallery-zoom]');
      const target = overlay?.querySelector('[data-gallery-zoom-image]');
      if (!overlay || !target || !(image instanceof HTMLImageElement)) return;

      target.src = image.currentSrc || image.src || '';
      target.srcset = image.srcset || '';
      target.sizes = image.sizes || '';
      target.alt = image.alt || 'Product image';
      this.openDialog('[data-gallery-zoom]');
      this.track('gallery_zoom_opened');
    }

    initialiseMobileDock() {
      const dock = this.querySelector('[data-mobile-dock]');
      const lowerContent = this.querySelector('[data-lower-content]');
      if (!dock || !lowerContent || !('IntersectionObserver' in window)) return;

      this.mobileDockObserver?.disconnect();
      this.mobileDockObserver = new IntersectionObserver((entries) => {
        const retire = entries.some((entry) => entry.isIntersecting);
        dock.classList.toggle('is-retired', retire);
        dock.setAttribute('aria-hidden', String(retire));
      }, {
        root: null,
        rootMargin: '0px 0px -28% 0px',
        threshold: 0
      });

      this.mobileDockObserver.observe(lowerContent);
    }

    initialiseStamped() {
      const wrapper = this.querySelector('[data-stamped-rating]');
      const badge = wrapper?.querySelector('.stamped-product-reviews-badge');
      const fallback = wrapper?.querySelector('[data-stamped-fallback]');
      if (!wrapper || !badge || !fallback) return;

      const sync = () => {
        const hasBadge = badge.querySelector('.stamped-badge-star, .stamped-fa-star, .stamped-badge-caption')
          || String(badge.textContent || '').trim().length > 4;
        fallback.hidden = Boolean(hasBadge);
      };

      this.stampedBadgeObserver?.disconnect();
      this.stampedBadgeObserver = new MutationObserver(sync);
      this.stampedBadgeObserver.observe(badge, { subtree: true, childList: true, characterData: true });
      [500, 1800].forEach((delay) => {
        const timer = window.setTimeout(sync, delay);
        this.zeptoInitTimers.push(timer);
      });
    }

    resolveVariant() {
      if (this.isStepEnabled(1) && !this.state.hand) {
        this.state.variant = null;
        return null;
      }

      if (this.isStepEnabled(2) && !this.state.size) {
        this.state.variant = null;
        return null;
      }

      const matching = this.variants.filter((candidate) => {
        const handMatches = !this.isStepEnabled(1)
          || this.handIndex < 0
          || normalise(candidate.options?.[this.handIndex]) === normalise(this.state.hand);
        const sizeMatches = !this.isStepEnabled(2)
          || this.sizeIndex < 0
          || normalise(candidate.options?.[this.sizeIndex]) === normalise(this.state.size);
        return handMatches && sizeMatches;
      });

      const variant = matching.find((candidate) => candidate.available)
        || matching[0]
        || (!this.isStepEnabled(1) && !this.isStepEnabled(2)
          ? this.variants.find((candidate) => candidate.available) || this.variants[0]
          : null);

      this.state.variant = variant || null;

      if (variant?.featured_media?.id && !this.userSelectedMedia) {
        this.activateMedia(String(variant.featured_media.id));
      }

      if (variant && this.variantInput) {
        this.variantInput.value = String(variant.id);
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      return variant;
    }

    updateSizeAvailability() {
      if (!this.isStepEnabled(2)) return;

      this.querySelectorAll('[data-size-value]').forEach((button) => {
        const sizeValue = button.dataset.sizeValue || '';
        const available = this.variants.some((candidate) => {
          const handMatches = !this.isStepEnabled(1)
            || !this.state.hand
            || this.handIndex < 0
            || normalise(candidate.options?.[this.handIndex]) === normalise(this.state.hand);
          const sizeMatches = this.sizeIndex < 0
            || normalise(candidate.options?.[this.sizeIndex]) === normalise(sizeValue);
          return handMatches && sizeMatches && candidate.available;
        });

        button.disabled = !available;
      });
    }

    observeZepto() {
      if (!this.zeptoContainer) return;

      this.zeptoObserver?.disconnect();
      this.zeptoObserver = new MutationObserver((mutations) => {
        const contentChanged = mutations.some((mutation) => mutation.type === 'childList');
        if (!contentChanged) return;

        // Decorate Zepto's EXISTING controls immediately after insertion.
        // Nothing is recreated and there is no delayed visual re-render.
        this.styleZeptoColourControls();
        this.scheduleZeptoCheck();
      });

      this.zeptoObserver.observe(this.zeptoContainer, {
        subtree: true,
        childList: true
      });

      this.styleZeptoColourControls();

      // Short safety passes only for apps that render nested markup in stages.
      [40, 120, 300, 700].forEach((delay) => {
        const timer = window.setTimeout(() => {
          this.styleZeptoColourControls();
          this.scheduleZeptoCheck();
        }, delay);
        this.zeptoInitTimers.push(timer);
      });
    }

    scheduleZeptoCheck() {
      window.clearTimeout(this.zeptoDebounce);
      this.zeptoDebounce = window.setTimeout(() => this.checkZepto(), 220);
    }

    isZeptoEventTarget(target) {
      if (!(target instanceof Element)) return false;
      if (this.zeptoContainer?.contains(target)) return true;
      return Boolean(target.closest?.('[data-zepto-app]'))
        || Boolean(target.matches?.('[name^="properties["]'));
    }

    zeptoDescriptor(element) {
      if (!(element instanceof Element)) return '';

      const parts = [
        element.getAttribute('name'),
        element.id,
        element.getAttribute('aria-label'),
        element.getAttribute('placeholder'),
        element.getAttribute('title'),
        element.getAttribute('data-label'),
        element.getAttribute('data-option-name')
      ];

      if (element.id) {
        const label = this.form?.querySelector(`label[for="${CSS.escape(element.id)}"]`);
        if (label) parts.push(label.textContent);
      }

      const labelledBy = element.getAttribute('aria-labelledby');
      if (labelledBy) {
        labelledBy.split(/\s+/).forEach((id) => {
          const label = document.getElementById(id);
          if (label) parts.push(label.textContent);
        });
      }

      const wrapper = element.closest(
        'label, fieldset, [class*="option" i], [class*="field" i], [class*="choice" i], [class*="personal" i]'
      );
      if (wrapper) parts.push(String(wrapper.textContent || '').slice(0, 220));

      return normalise(parts.filter(Boolean).join(' '));
    }

    zeptoContextText(element) {
      if (!(element instanceof Element)) return '';

      const parts = [this.zeptoDescriptor(element)];

      const localWrapper = element.closest(
        'label, fieldset, [class*="option" i], [class*="field" i], [class*="choice" i], [class*="swatch" i], [class*="group" i]'
      );

      if (localWrapper && localWrapper !== this.zeptoContainer) {
        parts.push(String(localWrapper.textContent || '').slice(0, 260));

        const previous = localWrapper.previousElementSibling;
        if (previous) parts.push(String(previous.textContent || '').slice(0, 180));
      } else {
        const parent = element.parentElement;
        if (parent && parent !== this.zeptoContainer) {
          parts.push(String(parent.textContent || '').slice(0, 220));

          const previous = parent.previousElementSibling;
          if (previous) parts.push(String(previous.textContent || '').slice(0, 160));
        }
      }

      return normalise(parts.filter(Boolean).join(' '));
    }

    zeptoDirectText(control) {
      if (!(control instanceof Element)) return '';

      const parts = [
        control.getAttribute('name'),
        control.id,
        control.getAttribute('aria-label'),
        control.getAttribute('placeholder'),
        control.getAttribute('title'),
        control.getAttribute('data-label'),
        control.getAttribute('data-option-name')
      ];

      if (control.id) {
        try {
          const label = this.form?.querySelector(`label[for="${CSS.escape(control.id)}"]`);
          if (label) parts.push(label.textContent);
        } catch (error) {}
      }

      const ownLabel = control.closest('label');
      if (ownLabel) parts.push(String(ownLabel.textContent || '').slice(0, 180));

      return normalise(parts.filter(Boolean).join(' '));
    }

    isZeptoColourControl(control) {
      if (!(control instanceof Element)) return false;

      const direct = this.zeptoDirectText(control);
      const local = this.zeptoContextText(control);
      const value = normalise(
        control instanceof HTMLInputElement
        || control instanceof HTMLSelectElement
        || control instanceof HTMLTextAreaElement
          ? control.value
          : control.getAttribute('data-value') || ''
      );

      // An explicitly named personalisation-text field must never become a
      // colour field merely because a parent wrapper also contains swatches.
      if (/\b(initials?|monogram|personalisation text|personalization text|engraving text|custom text|letters?)\b/i.test(direct)) {
        return false;
      }

      if (/\b(personalisation colou?r|personalization colou?r|text colou?r|foil colou?r|foil color|pick your.*colou?r|colou?r|foil|finish)\b/i.test(direct)) {
        return true;
      }

      if (
        (control.type === 'radio' || control.type === 'checkbox' || control.tagName === 'SELECT')
        && /\b(personalisation colou?r|personalization colou?r|text colou?r|foil colou?r|foil color|pick your.*colou?r|colou?r|foil|finish)\b/i.test(local)
      ) {
        return true;
      }

      // Hidden Zepto property fields sometimes have generic names but store a
      // value such as "Silver Foil" or "Gold Foil".
      if (
        this.isZeptoProperty(control)
        && /\b(gold|silver|rose gold|black|white|grey|gray|red|green|blue|pink|navy|brown|bronze|copper)\b.*\b(foil|imprint|print)?\b/i.test(value)
      ) {
        return true;
      }

      return false;
    }

    isZeptoTextControl(control) {
      if (!(control instanceof Element)) return false;

      const direct = this.zeptoDirectText(control);
      if (/\b(initials?|monogram|personalisation text|personalization text|engraving text|custom text|letters?)\b/i.test(direct)) {
        return true;
      }

      if (this.isZeptoColourControl(control)) return false;

      const local = this.zeptoContextText(control);
      return (
        (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement)
        && !['radio', 'checkbox', 'hidden'].includes(control.type)
        && /\b(initials?|monogram|personalisation text|personalization text|engraving text|custom text|letters?)\b/i.test(local)
      );
    }

    isPersonalisationTextPropertyKey(key) {
      return /\b(initials?|monogram|personalisation text|personalization text|engraving text|custom text|letters?)\b/i.test(
        normalise(String(key || ''))
      );
    }

    uppercaseValue(value) {
      return String(value || '').toLocaleUpperCase('en-AU');
    }

    uppercaseZeptoTextControl(control, dispatch = false) {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement)) return false;
      if (!this.isZeptoTextControl(control)) return false;

      const current = String(control.value || '');
      const upper = this.uppercaseValue(current);
      if (current === upper) return false;

      const start = typeof control.selectionStart === 'number' ? control.selectionStart : null;
      const end = typeof control.selectionEnd === 'number' ? control.selectionEnd : null;

      control.value = upper;

      if (start !== null && end !== null) {
        try { control.setSelectionRange(start, end); } catch (error) {}
      }

      if (dispatch) {
        control.dispatchEvent(new Event('input', { bubbles: true }));
        control.dispatchEvent(new Event('change', { bubbles: true }));
      }

      return true;
    }

    uppercaseAllZeptoPersonalisationText(dispatch = false) {
      this.getZeptoControls().forEach((control) => {
        this.uppercaseZeptoTextControl(control, dispatch);
      });
    }

    zeptoVisualChoice(element) {
      if (!(element instanceof Element)) return null;

      if (element.matches('input[type="radio"], input[type="checkbox"]')) {
        if (element.id) {
          try {
            const label = this.zeptoContainer?.querySelector(`label[for="${CSS.escape(element.id)}"]`);
            if (label) return label;
          } catch (error) {}
        }
        return element.closest('label') || element.parentElement;
      }

      return element.closest('button, [role="button"], label') || element;
    }

    zeptoLinkedInput(label) {
      if (!(label instanceof Element) || label.tagName !== 'LABEL') return null;
      const nested = label.querySelector('input[type="radio"], input[type="checkbox"]');
      if (nested) return nested;
      const forId = label.getAttribute('for');
      if (!forId) return null;
      try { return this.zeptoContainer?.querySelector(`#${CSS.escape(forId)}`) || null; }
      catch (error) { return null; }
    }

    isZeptoVisualSwatch(element) {
      if (!(element instanceof Element)) return false;
      if (element.matches('input[type="radio"], input[type="checkbox"]')) return true;
      const text = normalise(element.textContent || '');
      const classText = normalise(`${element.className || ''} ${element.getAttribute('data-value') || ''} ${element.getAttribute('aria-label') || ''} ${element.getAttribute('title') || ''}`);
      const inlineStyle = `${element.getAttribute('style') || ''} ${element.querySelector?.('[style]')?.getAttribute('style') || ''}`;
      const isPrompt = /\b(pick|choose|select).*(colou?r|foil|finish)\b/i.test(text)
        || /\bpersonalisation colou?r\b|\bpersonalization color\b/i.test(text);
      if (isPrompt && text.length > 14 && !element.querySelector('input')) return false;
      return text.length <= 18
        || /\b(swatch|colou?r|foil|finish)\b/i.test(classText)
        || /\b(gold|silver|black|white|grey|gray|red|green|blue|pink|rose|bronze|copper)\b/i.test(classText)
        || /background(?:-color)?\s*:/i.test(inlineStyle);
    }

    zeptoColourCandidates() {
      if (!this.zeptoContainer) return [];
      const candidates = new Set();
      this.zeptoContainer.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((input) => {
        if (this.isZeptoColourControl(input)) candidates.add(input);
      });
      this.zeptoContainer.querySelectorAll('label').forEach((label) => {
        const linked = this.zeptoLinkedInput(label);
        if (linked && this.isZeptoColourControl(linked)) candidates.add(linked);
      });
      this.zeptoContainer.querySelectorAll('button, [role="button"]').forEach((element) => {
        if (this.isZeptoColourControl(element) && this.isZeptoVisualSwatch(element)) candidates.add(element);
      });
      return [...candidates];
    }

    formatZeptoColourLabel(value) {
      let label = normalise(value || '');
      if (!label) return '';

      label = label
        .replace(/^[-–—:|,\s]+|[-–—:|,\s]+$/g, '')
        .replace(/[_]+/g, ' ')
        .replace(/\s*\/\s*/g, ' / ')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .replace(/(^|[\s/(-])([a-z])/g, (match, prefix, letter) => `${prefix}${letter.toUpperCase()}`);

      // Common personalisation terminology should always be presented with
      // consistent title casing regardless of Zepto's source formatting.
      label = label
        .replace(/\bFoil\b/gi, 'Foil')
        .replace(/\bRose Gold\b/gi, 'Rose Gold')
        .replace(/\bGold\b/gi, 'Gold')
        .replace(/\bSilver\b/gi, 'Silver');

      return label;
    }

    selectedZeptoColourLabel(group = null) {
      const groupKey = group?.key || 'personalisation-colour-1';
      if (!this.zeptoConfirmedColourGroups.has(groupKey)) return '';

      const clickedLabel = this.formatZeptoColourLabel(
        this.zeptoColourLabels.get(groupKey) || ''
      );
      if (clickedLabel) return clickedLabel;

      const controls = this.getZeptoControls();
      const colourProperties = controls.filter((control) => (
        this.isZeptoProperty(control)
        && this.isZeptoColourControl(control)
        && this.controlHasValue(control, controls)
      ));

      const control = colourProperties[group?.index || 0] || colourProperties[0];
      if (!control) return '';

      return this.formatZeptoColourLabel(
        this.zeptoColourChoiceLabel(control) || control.value
      );
    }

    zeptoTextControlForColourGroup(group, textControls = []) {
      if (!group?.prompt || !textControls.length) return null;

      const preceding = textControls.filter((control) => {
        if (!(control instanceof Element)) return false;
        return Boolean(
          control.compareDocumentPosition(group.prompt)
          & Node.DOCUMENT_POSITION_FOLLOWING
        );
      });

      if (!preceding.length) return textControls[group.index] || textControls[0] || null;

      // The matching field is the nearest visible personalisation text input
      // before this colour prompt. This correctly maps passport-holder text to
      // colour group 1 and luggage-tag text to colour group 2.
      return preceding[preceding.length - 1];
    }

    updateZeptoColourGuidance(textControls = null, colourRegions = null) {
      if (!this.zeptoContainer) return;

      const controls = this.getZeptoControls();
      const visibleTextControls = Array.isArray(textControls)
        ? textControls
        : controls.filter((control) => (
          this.isZeptoTextControl(control)
          && visible(control)
          && control.type !== 'hidden'
        ));

      const regions = Array.isArray(colourRegions)
        ? colourRegions
        : this.zeptoColourRegions();

      const activePrompts = new Set(regions.map((group) => group.prompt));

      this.zeptoContainer
        .querySelectorAll('.mg-zepto-colour-required-message')
        .forEach((message) => {
          if (!activePrompts.has(message.parentElement)) message.remove();
        });

      regions.forEach((group) => {
        const prompt = group.prompt;
        const textControl = this.zeptoTextControlForColourGroup(
          group,
          visibleTextControls
        );

        const textEntered = Boolean(
          textControl
          && this.controlHasValue(textControl, controls)
        );

        const colourConfirmed = this.zeptoConfirmedColourGroups.has(group.key);
        const needsColour = Boolean(
          this.requirePersonalisationColour
          && textEntered
          && !colourConfirmed
        );

        prompt.classList.toggle('mg-zepto-colour-prompt-required', needsColour);

        group.tiles.forEach((tile) => {
          tile.classList.toggle('mg-zepto-colour-choice-required', needsColour);
        });

        let message = prompt.querySelector(
          ':scope > .mg-zepto-colour-required-message'
        );

        if (!needsColour) {
          message?.remove();
          return;
        }

        if (!message) {
          message = document.createElement('span');
          message.className = 'mg-zepto-colour-required-message';
          prompt.appendChild(message);
        }

        // Keep the Zepto prompt itself unchanged. A small status chip provides
        // the next-action cue without flashing/replacing instructional copy.
        message.textContent = 'Selection required';
      });
    }

    updateZeptoSelectedColourLabel() {
      if (!this.zeptoContainer) return;

      const regions = this.zeptoColourRegions();
      const activePrompts = new Set(regions.map((group) => group.prompt));

      this.zeptoContainer
        .querySelectorAll('.mg-zepto-selected-colour-value')
        .forEach((element) => {
          if (!activePrompts.has(element.parentElement)) element.remove();
        });

      regions.forEach((group) => {
        const prompt = group.prompt;
        let value = prompt.querySelector(':scope > .mg-zepto-selected-colour-value');
        const colourLabel = this.selectedZeptoColourLabel(group);

        if (!this.zeptoConfirmedColourGroups.has(group.key) || !colourLabel) {
          value?.remove();
          return;
        }

        if (!value) {
          value = document.createElement('span');
          value.className = 'mg-zepto-selected-colour-value';
          value.setAttribute('aria-live', 'polite');
          prompt.appendChild(value);
        }

        value.textContent = `— ${colourLabel}`;
        value.setAttribute(
          'aria-label',
          `Selected personalisation colour: ${colourLabel}`
        );
      });
    }

    zeptoColourChoiceLabel(element) {
      if (!(element instanceof Element)) return '';

      const values = [];
      const add = (value) => {
        const clean = normalise(value || '');
        if (!clean) return;
        if (/^(?:on|true|false|1|0)$/i.test(clean)) return;
        if (/^#[0-9a-f]{3,8}$/i.test(clean)) return;
        if (/^rgba?\(/i.test(clean)) return;
        if (this.zeptoColourPromptPattern().test(clean)) return;
        if (clean.length > 60) return;
        values.push(clean);
      };

      [
        'title',
        'aria-label',
        'data-label',
        'data-value',
        'data-option-value',
        'data-name',
        'data-title'
      ].forEach((attribute) => add(element.getAttribute(attribute)));

      if (
        element instanceof HTMLInputElement
        || element instanceof HTMLSelectElement
        || element instanceof HTMLTextAreaElement
      ) {
        add(element.value);
      }

      const linkedInput = element.matches('label')
        ? this.zeptoLinkedInput(element)
        : null;

      if (linkedInput) {
        [
          'title',
          'aria-label',
          'data-label',
          'data-value',
          'data-option-value'
        ].forEach((attribute) => add(linkedInput.getAttribute(attribute)));
        add(linkedInput.value);
      }

      const nestedControl = element.querySelector?.(
        'input[type="radio"], input[type="checkbox"], input[type="hidden"], [data-value], [aria-label], [title]'
      );

      if (nestedControl && nestedControl !== element) {
        [
          'title',
          'aria-label',
          'data-label',
          'data-value',
          'data-option-value'
        ].forEach((attribute) => add(nestedControl.getAttribute(attribute)));

        if (
          nestedControl instanceof HTMLInputElement
          || nestedControl instanceof HTMLSelectElement
          || nestedControl instanceof HTMLTextAreaElement
        ) {
          add(nestedControl.value);
        }
      }

      const ownText = normalise(element.textContent || '');
      if (ownText && ownText.length <= 36) add(ownText);

      const colourName = values.find((value) => (
        /\b(?:gold|silver|rose gold|black|white|grey|gray|red|green|blue|pink|navy|brown|bronze|copper|tan|olive|ivory|cream)\b/i.test(value)
        || /\b(?:foil|imprint|print|finish)\b/i.test(value)
      ));

      return colourName || values[0] || '';
    }

    applyZeptoColourTooltip(visual, source = null) {
      if (!(visual instanceof Element)) return;

      let label = this.zeptoColourChoiceLabel(source || visual)
        || this.zeptoColourChoiceLabel(visual);

      if (!label) {
        const descendant = visual.querySelector?.(
          '[data-value], [data-label], [aria-label], [title], input, option'
        );
        if (descendant) label = this.zeptoColourChoiceLabel(descendant);
      }

      if (!label) return;

      label = this.formatZeptoColourLabel(label);
      if (!label) return;

      visual.setAttribute('title', label);
      visual.setAttribute('aria-label', label);
      visual.dataset.mgColourLabel = label;
    }

    styleZeptoColourControls() {
      const candidates = this.zeptoColourCandidates();
      const candidatePairs = candidates
        .map((control) => ({
          control,
          visual: this.zeptoVisualChoice(control)
        }))
        .filter((pair) => Boolean(pair.visual));

      const candidateVisuals = candidatePairs.map((pair) => pair.visual);
      const fallbackVisuals = this.zeptoColourRegionTiles();
      const visuals = new Set([...candidateVisuals, ...fallbackVisuals]);

      this.zeptoContainer?.querySelectorAll('.mg-zepto-native-colour-choice').forEach((visual) => {
        if (!visuals.has(visual)) {
          visual.classList.remove('mg-zepto-native-colour-choice');
          visual.removeAttribute('data-mg-colour-label');
        }
      });

      visuals.forEach((visual) => {
        if (!visual.classList.contains('mg-zepto-native-colour-choice')) {
          visual.classList.add('mg-zepto-native-colour-choice');
        }

        const pair = candidatePairs.find((candidate) => candidate.visual === visual);
        this.applyZeptoColourTooltip(visual, pair?.control || visual);
      });

      // When Zepto separates the visible tile and underlying input/property,
      // use the native input only for its label. No value/selection is changed.
      if (candidatePairs.length && fallbackVisuals.length) {
        fallbackVisuals.forEach((visual, index) => {
          const pair = candidatePairs[index];
          if (pair) this.applyZeptoColourTooltip(visual, pair.control);
        });
      }
    }

    zeptoColourPromptPattern() {
      // Zepto can suffix repeated option names directly with a number, e.g.
      // "Pick Your Personalisation Colour2". Treat Colour, Colour 2 and
      // Colour2 as separate valid colour prompts.
      return /\b(?:pick|choose|select).{0,45}(?:personalisation|personalization|text)?\s*(?:colou?r|color|foil|finish)(?:\s*#?\d+)?\b|\b(?:personalisation|personalization|text)\s*(?:colou?r|color)(?:\s*#?\d+)?\b|\bfoil\s*(?:colou?r|color)(?:\s*#?\d+)?\b/i;
    }

    zeptoColourPromptBaseText(element) {
      if (!(element instanceof Element)) return '';

      const clone = element.cloneNode(true);
      clone.querySelectorAll?.(
        '.mg-zepto-selected-colour-value, .mg-zepto-colour-required-message'
      ).forEach((node) => node.remove());
      return normalise(clone.textContent || '');
    }

    zeptoColourPromptElements() {
      if (!this.zeptoContainer) return [];

      const selector = 'label, legend, p, span, div, strong, small';
      const candidates = [...this.zeptoContainer.querySelectorAll(selector)]
        .filter((element) => {
          const text = this.zeptoColourPromptBaseText(element);
          if (!text || text.length > 100) return false;
          return this.zeptoColourPromptPattern().test(text);
        });

      // Keep the deepest matching prompt only. This prevents a parent wrapper
      // containing two personalisation areas from being mistaken for one group.
      return candidates
        .filter((element) => !candidates.some((candidate) => (
          candidate !== element && element.contains(candidate)
        )))
        .sort((a, b) => {
          const position = a.compareDocumentPosition(b);
          if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
          if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
          return 0;
        });
    }

    zeptoColourGroupKey(prompt, index = 0) {
      const base = this.zeptoColourPromptBaseText(prompt)
        .toLowerCase()
        .replace(/^pick\s+your\s+/i, '')
        .replace(/^choose\s+your\s+/i, '')
        .replace(/^select\s+your\s+/i, '')
        .replace(/\b(colou?r|color)\s*(\d+)\b/gi, '$1-$2')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      // The positional suffix guarantees uniqueness even if Zepto repeats the
      // exact same visible prompt text for multiple components.
      return `${base || 'personalisation-colour'}-${index + 1}`;
    }

    zeptoColourRegions() {
      if (!this.zeptoContainer) return [];

      const prompts = this.zeptoColourPromptElements();
      if (!prompts.length) return [];

      const allVisuals = [...this.zeptoContainer.querySelectorAll('*')]
        .filter((element) => this.isZeptoSquareColourVisual(element));

      return prompts.map((prompt, index) => {
        const nextPrompt = prompts[index + 1] || null;
        const rawTiles = allVisuals.filter((element) => {
          const afterPrompt = Boolean(
            prompt.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING
          );
          if (!afterPrompt) return false;

          if (nextPrompt) {
            const beforeNext = Boolean(
              element.compareDocumentPosition(nextPrompt) & Node.DOCUMENT_POSITION_FOLLOWING
            );
            if (!beforeNext) return false;
          }

          return true;
        });

        const tiles = [];
        const seen = new Set();

        rawTiles.forEach((visual) => {
          let surface = visual.closest(
            'button, label, [role="button"], [role="option"], [tabindex], [class*="swatch" i], [class*="choice" i], [class*="option" i]'
          );

          if (!surface || !this.zeptoContainer.contains(surface)) surface = visual;

          const rect = surface.getBoundingClientRect();
          if (rect.width > 110 || rect.height > 110) surface = visual;

          if (!seen.has(surface)) {
            seen.add(surface);
            tiles.push(surface);
          }
        });

        return {
          key: this.zeptoColourGroupKey(prompt, index),
          index,
          prompt,
          tiles
        };
      }).filter((group) => group.tiles.length >= 2);
    }

    zeptoColourRegionTiles() {
      return this.zeptoColourRegions().flatMap((group) => group.tiles);
    }

    zeptoColourGroupForTarget(target) {
      if (!(target instanceof Element)) return null;

      return this.zeptoColourRegions().find((group) => (
        group.tiles.some((tile) => tile === target || tile.contains(target))
      )) || null;
    }

    isZeptoSquareColourVisual(element) {
      if (!(element instanceof Element)) return false;
      if (!visible(element)) return false;

      if (element.matches('input[type="radio"], input[type="checkbox"]')) {
        return true;
      }

      if (element.matches('input, textarea, select')) return false;

      const rect = element.getBoundingClientRect();
      if (
        rect.width < 14
        || rect.height < 14
        || rect.width > 82
        || rect.height > 82
      ) {
        return false;
      }

      const ratio = rect.width / Math.max(1, rect.height);
      if (ratio < 0.5 || ratio > 2) return false;

      const text = normalise(element.textContent || '');
      if (text.length > 18) return false;

      const style = window.getComputedStyle(element);
      const classText = normalise(
        `${element.className || ''} ${element.getAttribute('data-value') || ''} ${element.getAttribute('aria-label') || ''} ${element.getAttribute('title') || ''}`
      );
      const background = String(style.backgroundColor || '');
      const backgroundImage = String(style.backgroundImage || '');
      const borderWidth = ['Top', 'Right', 'Bottom', 'Left']
        .some((side) => Number.parseFloat(style[`border${side}Width`] || '0') > 0);

      const hasVisibleBackground = background
        && background !== 'rgba(0, 0, 0, 0)'
        && background !== 'transparent';

      const hasColourName = /\b(gold|silver|black|white|grey|gray|red|green|blue|pink|rose|navy|brown|bronze|copper|tan|olive|ivory|cream)\b/i.test(classText);

      const hasSwatchClass = /\b(swatch|colou?r|color|foil|finish|palette)\b/i.test(classText);

      return hasVisibleBackground
        || (backgroundImage && backgroundImage !== 'none')
        || hasColourName
        || hasSwatchClass
        || borderWidth;
    }


    resolveZeptoColourSwatch(target) {
      if (!(target instanceof Element) || !this.zeptoContainer?.contains(target)) return null;

      const recognised = target.closest('.mg-zepto-native-colour-choice');
      if (recognised && this.zeptoContainer.contains(recognised)) return recognised;

      const directInput = target.closest('input[type="radio"], input[type="checkbox"], select');
      if (directInput && this.isZeptoColourControl(directInput)) {
        return this.zeptoVisualChoice(directInput) || directInput;
      }

      const tiles = this.zeptoColourRegionTiles();
      if (!tiles.length) return null;

      // Only the clicked tile itself or a descendant qualifies. A parent
      // wrapper containing text + every swatch never counts as a colour click.
      const matches = tiles.filter((tile) => tile === target || tile.contains(target));
      if (!matches.length) return null;

      matches.sort((a, b) => {
        const ar = a.getBoundingClientRect();
        const br = b.getBoundingClientRect();
        return (ar.width * ar.height) - (br.width * br.height);
      });

      return matches[0];
    }

    confirmZeptoColourInteraction(target, interactionType = '', trustedInteraction = false) {
      if (!trustedInteraction) return false;
      if (!['click', 'change'].includes(interactionType)) return false;

      const nativeSwatch = this.resolveZeptoColourSwatch(target);
      if (!nativeSwatch) return false;
      if (nativeSwatch.matches(':disabled, [aria-disabled="true"]')) return false;

      const colourRegions = this.zeptoColourRegions();
      const group = this.zeptoColourGroupForTarget(nativeSwatch)
        || this.zeptoColourGroupForTarget(target);

      // If Zepto exposes multiple colour prompts, never guess group 1. A click
      // must resolve to the actual group it occurred inside.
      if (colourRegions.length > 1 && !group) return false;

      const groupKey = group?.key || colourRegions[0]?.key || 'personalisation-colour-1';

      // Zepto owns each selected value. We record explicit shopper intent
      // independently for every personalisation-colour group.
      this.zeptoConfirmedColourGroups.add(groupKey);
      this.state.zeptoTouched = true;
      this.state.zeptoColourTouched = this.zeptoConfirmedColourGroups.size > 0;

      const nativeLabel = this.formatZeptoColourLabel(
        this.zeptoColourChoiceLabel(nativeSwatch)
      );
      if (nativeLabel) {
        this.zeptoLastColourLabel = nativeLabel;
        this.zeptoColourLabels.set(groupKey, nativeLabel);
      }

      // Mobile cannot rely on hover/title attributes. Each personalisation
      // area gets its own selected-colour text beside its own Zepto prompt.
      this.updateZeptoSelectedColourLabel();
      this.updateZeptoColourGuidance();
      return true;
    }

    noteZeptoInteraction(target, interactionType = '', trustedInteraction = false) {
      if (!(target instanceof Element)) return;

      if (trustedInteraction) this.state.zeptoTouched = true;

      // Text and colour use separate validation paths. A click/focus anywhere
      // around the text field can never satisfy the colour requirement.
      if (
        trustedInteraction
        && (interactionType === 'input' || interactionType === 'change')
        && this.isZeptoTextControl(target)
      ) {
        this.state.zeptoTextTouched = true;
      }

      const relatedTextControl = target.closest('label')?.querySelector('input:not([type="radio"]):not([type="checkbox"]), textarea')
        || target.closest('[class*="field" i]')?.querySelector('input:not([type="radio"]):not([type="checkbox"]), textarea');

      if (
        trustedInteraction
        && relatedTextControl
        && this.isZeptoTextControl(relatedTextControl)
      ) {
        this.state.zeptoTextTouched = true;
      }

      if (
        interactionType === 'click'
        || interactionType === 'change'
        || interactionType === 'input'
      ) {
        this.scheduleZeptoCheck();
      }
    }

    bindZeptoCapture() {
      if (!this.zeptoContainer || this.zeptoCaptureHandler) return;

      this.zeptoCaptureHandler = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        if (event.type === 'input' || event.type === 'change') {
          this.uppercaseZeptoTextControl(target, false);
        }

        this.confirmZeptoColourInteraction(target, event.type, event.isTrusted);
        this.noteZeptoInteraction(target, event.type, event.isTrusted);

        if (event.type === 'pointerdown' || event.type === 'click') {
          window.setTimeout(() => {
            this.styleZeptoColourControls();
            this.checkZepto();
          }, 70);

          window.setTimeout(() => {
            this.styleZeptoColourControls();
            this.checkZepto();
          }, 220);

          window.setTimeout(() => {
            this.styleZeptoColourControls();
            this.checkZepto();
          }, 500);
        } else if (event.type === 'input' || event.type === 'change') {
          this.scheduleZeptoCheck();
        }
      };

      ['pointerdown', 'click', 'change', 'input'].forEach((eventName) => {
        this.zeptoContainer.addEventListener(eventName, this.zeptoCaptureHandler, true);
      });
    }

    unbindZeptoCapture() {
      if (!this.zeptoCaptureHandler || !this.zeptoContainer) return;

      ['pointerdown', 'click', 'change', 'input'].forEach((eventName) => {
        this.zeptoContainer.removeEventListener(eventName, this.zeptoCaptureHandler, true);
      });

      this.zeptoCaptureHandler = null;
    }

    getZeptoControls() {
      const controls = new Set();

      this.zeptoContainer?.querySelectorAll('input, select, textarea').forEach((control) => controls.add(control));
      this.form?.querySelectorAll('[name^="properties["]').forEach((control) => controls.add(control));

      return [...controls].filter((control) => {
        if (control === this.querySelector('[data-note-input]')) return false;
        if (control.type === 'submit' || control.type === 'button') return false;
        if (control.name === 'id' || control.name === 'quantity') return false;

        const property = String(control.name || '').startsWith('properties[');
        return property || visible(control);
      });
    }

    controlHasValue(control, controls) {
      if (control.type === 'checkbox') {
        return control.checked;
      }

      if (control.type === 'radio') {
        if (!control.name) return control.checked;
        const group = controls.filter((candidate) => candidate.type === 'radio' && candidate.name === control.name);
        return group.some((candidate) => candidate.checked);
      }

      if (control.tagName === 'SELECT' && control.multiple) {
        return [...control.selectedOptions].some((option) => option.value !== '');
      }

      return String(control.value || '').trim() !== '';
    }

    isZeptoProperty(control) {
      const name = String(control.name || '');
      if (!name.startsWith('properties[')) return false;
      if (control.matches('[data-note-input]')) return false;

      const key = name.replace(/^properties\[/, '').replace(/\]$/, '').trim();
      if (!key || key.toLowerCase() === 'gift note') return false;
      return true;
    }

    checkZepto() {
      const controls = this.getZeptoControls();
      const visibleControls = controls.filter((control) => visible(control) && control.type !== 'hidden');
      const requiredControls = visibleControls.filter((control) => control.required || control.getAttribute('aria-required') === 'true');
      const propertyControls = controls.filter((control) => this.isZeptoProperty(control));
      const populatedProperties = propertyControls.filter((control) => this.controlHasValue(control, controls));

      const requiredInvalid = requiredControls.some((control) => {
        if (!this.controlHasValue(control, controls)) return true;
        return typeof control.checkValidity === 'function' && !control.checkValidity();
      });

      const colourControls = controls.filter((control) => this.isZeptoColourControl(control));
      const textControls = controls.filter((control) => this.isZeptoTextControl(control));
      const visibleTextControls = textControls.filter((control) => (
        visible(control) && control.type !== 'hidden'
      ));
      const textPropertyControls = propertyControls.filter((control) => (
        this.isZeptoTextControl(control)
      ));
      const zeptoText = normalise(this.zeptoContainer?.textContent || '');

      const colourRegions = this.zeptoColourRegions();
      const colourUiDetected = colourControls.length > 0
        || colourRegions.length > 0
        || this.zeptoColourCandidates().length > 0
        || Boolean(this.zeptoContainer?.querySelector('.mg-zepto-native-colour-choice'))
        || /\b(personalisation colou?r|personalization colou?r|pick your.*colou?r|foil colou?r|foil color|text colou?r)\b/i.test(zeptoText);

      const textExpected = textControls.length > 0
        || /\b(personalisation text|personalization text|initials?|monogram)\b/i.test(zeptoText);

      // Multi-part products such as the passport set have more than one
      // personalisation text field. Every visible personalisation text field
      // must be completed; "one of them has a value" is no longer sufficient.
      const textComplete = textExpected
        ? (
          visibleTextControls.length > 0
            ? visibleTextControls.every((control) => this.controlHasValue(control, controls))
            : textPropertyControls.length > 0
              ? textPropertyControls.every((control) => this.controlHasValue(control, controls))
              : (
                this.state.zeptoTextTouched
                && populatedProperties.some((control) => !this.isZeptoColourControl(control))
              )
        )
        : populatedProperties.some((control) => !this.isZeptoColourControl(control));

      const colourPropertyControls = propertyControls.filter((control) => (
        this.isZeptoColourControl(control)
        && this.controlHasValue(control, controls)
      ));

      const selectedRadioGroups = new Set();
      colourControls.forEach((control) => {
        if (control.type === 'radio' && control.checked) {
          selectedRadioGroups.add(control.name || control.id || `radio-${selectedRadioGroups.size}`);
        }
      });

      const selectedStandaloneControls = colourControls.filter((control) => {
        if (control.type === 'radio') return false;
        if (control.type === 'checkbox') return Boolean(control.checked);
        if (control.tagName === 'SELECT') return this.controlHasValue(control, controls);
        if (this.isZeptoProperty(control)) return false;
        return false;
      });

      // Hidden Zepto line-item properties are the preferred count because two
      // personalisation areas may legitimately select the same value, e.g.
      // Gold Foil + Gold Foil. Values are counted by control, not deduplicated.
      const colourValueCount = colourPropertyControls.length > 0
        ? colourPropertyControls.length
        : selectedRadioGroups.size + selectedStandaloneControls.length;

      const requiredColourGroupCount = colourRegions.length > 0
        ? colourRegions.length
        : colourUiDetected ? 1 : 0;

      const confirmedColourGroupCount = colourRegions.length > 0
        ? colourRegions.filter((group) => this.zeptoConfirmedColourGroups.has(group.key)).length
        : this.state.zeptoColourTouched ? 1 : 0;

      const colourExpected = this.requirePersonalisationColour
        && requiredColourGroupCount > 0;

      const colourComplete = !colourExpected
        || (
          confirmedColourGroupCount >= requiredColourGroupCount
          && colourValueCount >= requiredColourGroupCount
        );

      this.state.zeptoColourTouched = this.zeptoConfirmedColourGroups.size > 0
        || (colourRegions.length === 0 && this.state.zeptoColourTouched);

      this.dataset.zeptoColourConfirmed = colourComplete && colourExpected
        ? 'true'
        : 'false';

      const requiredComplete = requiredControls.length === 0 || !requiredInvalid;
      const genericComplete = populatedProperties.length > 0
        || visibleControls.some((control) => this.controlHasValue(control, controls));

      const allComplete = !requiredInvalid
        && requiredComplete
        && (textExpected ? textComplete : genericComplete)
        && colourComplete;

      const changed = this.state.zeptoComplete !== allComplete;
      this.state.zeptoComplete = allComplete;

      if (changed && allComplete) {
        this.track('personalisation_completed', {
          summary: this.getZeptoSummary(),
          colour_groups_required: requiredColourGroupCount,
          colour_groups_confirmed: confirmedColourGroupCount
        });
      }

      if (allComplete) this.clearStepError(3);

      this.updateZeptoSelectedColourLabel();
      this.updateZeptoColourGuidance(visibleTextControls, colourRegions);
      this.update();
    }

    zeptoPropertyDisplayKey(control) {
      return normalise(
        String(control?.name || '')
          .replace(/^properties\[/, '')
          .replace(/\]$/, '')
          .replace(/([A-Za-z])([0-9]+)$/g, '$1 $2')
          .replace(/[_-]+/g, ' ')
      );
    }

    zeptoTextSubject(control) {
      const key = this.zeptoPropertyDisplayKey(control);
      if (!key) return '';

      const match = key.match(
        /personalisation\s+text(?:\s+for)?\s+(.+?)(?=\s+(?:up\s*to|upto|\d+\s*characters?)\b|$)/i
      );

      if (!match?.[1]) return '';

      return normalise(match[1])
        .replace(/\s*(?:letters?|numbers?|and|full\s+stops?|only)\b.*$/i, '')
        .replace(/[.:;,]+$/g, '')
        .trim()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    }

    getZeptoCustomerSummary() {
      const controls = this.getZeptoControls();

      // Customer-facing text comes from Zepto's actual editable text fields.
      // Do NOT require CSS visibility here: once Step 3 collapses, the genuine
      // text input is hidden by the accordion even though its value is still
      // the customer's selected personalisation. Hidden Shopify property
      // inputs remain excluded so they cannot duplicate the displayed text.
      const textValues = controls
        .filter((control) => {
          if (!this.zeptoContainer?.contains(control)) return false;
          if (!this.isZeptoTextControl(control)) return false;

          const editableTextControl = control.matches(
            'textarea, input:not([type]), input[type="text"], input[type="search"], input[type="tel"], input[type="email"]'
          );

          return editableTextControl
            && this.controlHasValue(control, controls);
        })
        .map((control) => this.uppercaseValue(String(control.value || '').trim()))
        .filter(Boolean);

      const colourValues = [];
      const regions = this.zeptoColourRegions();

      // One display colour per CURRENT Zepto colour region. Do not iterate the
      // entire saved colour-label map because older/stale group keys can remain
      // after Zepto re-renders and would display the same foil twice.
      regions.forEach((group) => {
        if (!this.zeptoConfirmedColourGroups.has(group.key)) return;

        const rawLabel = this.zeptoColourLabels.get(group.key)
          || this.selectedZeptoColourLabel(group);

        const label = this.formatZeptoColourLabel(rawLabel);
        if (label) colourValues.push(label);
      });

      // Fallback only when Zepto exposes no detectable visual colour regions.
      // Still use confirmed explicit colour labels only.
      if (!regions.length && !colourValues.length) {
        this.zeptoColourLabels.forEach((rawLabel, groupKey) => {
          if (!this.zeptoConfirmedColourGroups.has(groupKey)) return;
          const label = this.formatZeptoColourLabel(rawLabel);
          if (label) colourValues.push(label);
        });
      }

      const count = Math.max(textValues.length, colourValues.length);
      const groups = [];

      for (let index = 0; index < count; index += 1) {
        const text = textValues[index] || '';
        const colour = colourValues[index] || '';
        const combined = [text, colour].filter(Boolean).join(' / ');
        if (combined) groups.push(combined);
      }

      return groups.join(' · ');
    }

    getZeptoSummary() {
      const controls = this.getZeptoControls();
      const propertyControls = controls.filter((control) => this.isZeptoProperty(control));

      const groups = [];
      let currentGroup = null;
      const ungrouped = [];

      propertyControls.forEach((control) => {
        if ((control.type === 'checkbox' || control.type === 'radio') && !control.checked) return;

        let value = String(control.value || '').trim();
        if (!value || value.length > 100 || /^data:|^blob:|^https?:/i.test(value)) return;

        const rawKey = this.zeptoPropertyDisplayKey(control);
        if (!rawKey || rawKey.toLowerCase() === 'gift note') return;
        if (rawKey.startsWith('_')) return;
        if (/preview|image|upload|file/i.test(rawKey)) return;

        if (this.isZeptoTextControl(control)) {
          value = this.uppercaseValue(value);

          const subject = this.zeptoTextSubject(control)
            || `Personalisation ${groups.length + 1}`;

          currentGroup = {
            subject,
            text: value,
            colour: ''
          };
          groups.push(currentGroup);
          return;
        }

        if (this.isZeptoColourControl(control)) {
          value = this.formatZeptoColourLabel(value);

          // Zepto normally outputs colour immediately after the matching text
          // property. Attach it to that component instead of displaying
          // Zepto's long instructional property name in the customer summary.
          if (currentGroup && !currentGroup.colour) {
            currentGroup.colour = value;
          } else {
            ungrouped.push(value);
          }
          return;
        }

        // Preserve any other meaningful Zepto property without exposing a
        // paragraph-length configuration/instruction label.
        if (rawKey.length <= 32) {
          ungrouped.push(`${rawKey}: ${value}`);
        } else {
          ungrouped.push(value);
        }
      });

      const summaries = groups.map((group) => {
        const values = [group.text, group.colour].filter(Boolean).join(' / ');
        return `${group.subject}: ${values}`;
      });

      summaries.push(...ungrouped);

      if (summaries.length) return summaries.join(' · ');

      const fallbackValues = [];
      controls.forEach((control) => {
        if (!this.zeptoContainer?.contains(control)) return;
        if ((control.type === 'checkbox' || control.type === 'radio') && !control.checked) return;

        let value = String(control.value || '').trim();
        if (!value || value.length > 80 || /^data:|^blob:|^https?:/i.test(value)) return;

        if (this.isZeptoTextControl(control)) value = this.uppercaseValue(value);
        if (this.isZeptoColourControl(control)) value = this.formatZeptoColourLabel(value);

        fallbackValues.push(value);
      });

      return fallbackValues.join(' · ');
    }

    initialiseAddons() {
      this.querySelectorAll('[data-simple-addon-card]').forEach((card) => {
        this.clearAddonChoice(card);
        this.setAddonSelected(card, false);
      });
    }

    clearAddonChoice(card) {
      if (!card) return;

      card.querySelectorAll('[data-simple-addon-choice]').forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });

      card.dataset.addonVariantId = '';
      card.dataset.addonColour = '';
      card.dataset.addonProductUrl = '';

      const colourLabel = card.querySelector('[data-addon-colour-label]');
      if (colourLabel) {
        colourLabel.textContent = '';
        colourLabel.hidden = true;
      }

      const status = card.querySelector('[data-addon-status]');
      if (status) status.textContent = '';
    }

    applyAddonChoice(card, choice, announce = true) {
      if (!card || !choice) return;

      card.querySelectorAll('[data-simple-addon-choice]').forEach((button) => {
        const active = button === choice;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });

      card.dataset.addonVariantId = choice.dataset.choiceVariantId || '';
      card.dataset.addonLivePriceCents = choice.dataset.choiceLivePriceCents || '0';
      card.dataset.addonDisplayPriceCents = choice.dataset.choiceDisplayPriceCents || '0';
      card.dataset.addonRegularPriceCents = choice.dataset.choiceRegularPriceCents || '0';
      card.dataset.addonColour = choice.dataset.choiceLabel || '';
      card.dataset.addonProductUrl = choice.dataset.choiceProductUrl || '';

      const image = card.querySelector('.mg-simple-addon__image');
      if (image && choice.dataset.choiceImageUrl) {
        image.src = choice.dataset.choiceImageUrl;
        image.removeAttribute('srcset');
      }

      const link = card.querySelector('[data-addon-link]');
      if (link && choice.dataset.choiceProductUrl) {
        link.href = choice.dataset.choiceProductUrl;
      }

      const colourLabel = card.querySelector('[data-addon-colour-label]');
      if (colourLabel) {
        colourLabel.textContent = card.dataset.addonColour;
        colourLabel.hidden = !card.dataset.addonColour;
      }

      const display = Number(choice.dataset.choiceDisplayPriceCents || 0);
      const regular = Number(choice.dataset.choiceRegularPriceCents || 0);
      const saving = Math.max(0, regular - display);

      const price = card.querySelector('[data-addon-price]');
      const compare = card.querySelector('[data-addon-compare]');
      const savingNode = card.querySelector('[data-addon-saving]');

      if (price) price.textContent = this.formatMoney(display);

      if (compare) {
        compare.textContent = regular > display ? this.formatMoney(regular) : '';
        compare.classList.toggle('is-hidden', regular <= display);
      }

      if (savingNode) {
        savingNode.textContent = saving > 0 ? `Save ${this.formatMoney(saving)}` : '';
        savingNode.classList.toggle('is-hidden', saving <= 0);
      }

      const toggle = card.querySelector('[data-simple-addon-toggle]');
      const available = choice.dataset.choiceAvailable !== 'false';
      if (toggle) toggle.disabled = !available;

      if (announce) {
        const status = card.querySelector('[data-addon-status]');
        if (status) status.textContent = `${card.dataset.addonColour} selected.`;
      }
    }

    setAddonSelected(card, selected) {
      if (!card) return;

      card.dataset.addonSelected = selected ? 'true' : 'false';
      card.classList.toggle('is-selected', selected);

      const toggle = card.querySelector('[data-simple-addon-toggle]');
      const label = card.querySelector('[data-addon-toggle-label]');

      toggle?.setAttribute('aria-pressed', String(selected));
      if (label) {
        label.textContent = selected
          ? (card.dataset.addonIncludedLabel || 'Added ✓')
          : (card.dataset.addonButtonLabel || 'Add to order');
      }

      const status = card.querySelector('[data-addon-status]');
      if (status) status.textContent = '';

      if (selected) {
        card.classList.remove('is-highlighted');
        void card.offsetWidth;
        card.classList.add('is-highlighted');
      }
    }

    selectedAddonCards() {
      return [...this.querySelectorAll('[data-simple-addon-card]')]
        .filter((card) => {
          const toggle = card.querySelector('[data-simple-addon-toggle]');
          return card.dataset.addonSelected === 'true'
            || card.classList.contains('is-selected')
            || toggle?.getAttribute('aria-pressed') === 'true';
        });
    }

    addonDisplayTotal() {
      return this.selectedAddonCards().reduce((sum, card) => {
        return sum + Number(card.dataset.addonDisplayPriceCents || 0);
      }, 0);
    }

    addonRegularTotal() {
      return this.selectedAddonCards().reduce((sum, card) => {
        return sum + Number(card.dataset.addonRegularPriceCents || card.dataset.addonDisplayPriceCents || 0);
      }, 0);
    }

    addonSavingTotal() {
      return Math.max(0, this.addonRegularTotal() - this.addonDisplayTotal());
    }

    createGiftSetId() {
      return `MG-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
    }

    async resolveAddonVariantId(card) {
      const activeChoice = card?.querySelector('[data-simple-addon-choice].is-active')
        || card?.querySelector('[data-simple-addon-choice][aria-pressed="true"]');

      const direct = Number(
        activeChoice?.dataset.choiceVariantId
        || card?.dataset.addonVariantId
        || 0
      );

      if (Number.isInteger(direct) && direct > 0) return direct;

      const productUrl = String(
        activeChoice?.dataset.choiceProductUrl
        || card?.dataset.addonProductUrl
        || ''
      ).trim();

      if (!productUrl) return 0;

      try {
        const url = productUrl.endsWith('.js') ? productUrl : `${productUrl}.js`;
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) return 0;
        const product = await response.json();
        const variant = product?.variants?.find((candidate) => candidate.available)
          || product?.variants?.[0];
        return Number(variant?.id || 0);
      } catch (error) {
        console.warn('[Mr Golf] Could not resolve add-on variant.', error);
        return 0;
      }
    }

    async addonItems(giftSetId) {
      const cards = this.selectedAddonCards();
      const items = [];

      for (const card of cards) {
        const variantId = await this.resolveAddonVariantId(card);
        if (!Number.isInteger(variantId) || variantId <= 0) {
          throw new Error(`The selected ${card.dataset.addonTitle || 'gift-set add-on'} variant could not be resolved.`);
        }

        card.dataset.addonVariantId = String(variantId);
        const personalisation = String(card.dataset.addonPersonalisation || '')
          .trim()
          .replace(/^([^·]+)(?=\s*·|$)/, (match) => this.uppercaseValue(match.trim()));

        items.push({
          id: variantId,
          quantity: 1,
          properties: {
            '_Gift set ID': giftSetId,
            '_Added as': 'Product add-on',
            '_MG Edit URL': `${location.pathname}?continue_personalisation=1#${this.id}`,
            '_MG Configuration': `${card.dataset.addonTitle || 'Gift set add-on'} · ${card.dataset.addonColour || ''}`,
            'Selected colour': card.dataset.addonColour || '',
            'Personalisation': personalisation || 'Match the main personalised item'
          }
        });
      }

      if (cards.length && items.length !== cards.length) {
        throw new Error('The complete gift-set add-on selection could not be prepared.');
      }

      return items;
    }

    async addAddonLine(endpoint, item) {
      const formData = new FormData();
      formData.set('id', String(item.id));
      formData.set('quantity', String(item.quantity || 1));

      Object.entries(item.properties || {}).forEach(([key, value]) => {
        formData.set(`properties[${key}]`, String(value || ''));
      });

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'The gift-set add-on could not be added.');
      }

      return payload;
    }

    formProperties() {
      const properties = {};
      const formData = new FormData(this.form);

      for (const [name, rawValue] of formData.entries()) {
        const match = String(name).match(/^properties\[(.*)\]$/);
        if (!match) continue;

        if (rawValue instanceof File) {
          if (rawValue.size > 0) {
            throw new Error('Uploaded-file personalisation cannot be combined with this gift-set add-on.');
          }
          continue;
        }

        const key = match[1];
        let value = String(rawValue || '').trim();
        if (!value) continue;

        if (this.isPersonalisationTextPropertyKey(key)) {
          value = this.uppercaseValue(value);
        }

        properties[key] = value;
      }

      const noteText = this.state.note ? this.giftNoteText() : '';
      const notePropertyName = String(this.dataset.notePropertyName || 'Gift note').trim() || 'Gift note';
      if (noteText) {
        properties[notePropertyName] = noteText;
        properties['_MG Gift note'] = noteText;
      } else {
        delete properties[notePropertyName];
        delete properties['_MG Gift note'];
      }

      return properties;
    }

    mainProductItem(giftSetId, addOns = []) {
      const variantId = Number(this.variantInput?.value || this.state.variant?.id || 0);
      if (!variantId) throw new Error('Please select the product options before adding this order.');

      const properties = this.formProperties();
      properties['_Gift set ID'] = giftSetId;
      properties['_Gift set includes'] = addOns
        .map((item) => item.properties?.['_MG Configuration'])
        .filter(Boolean)
        .join(', ');

      const item = {
        id: variantId,
        quantity: Number(this.state.quantity || 1),
        properties
      };

      const sellingPlan = this.form?.querySelector('[name="selling_plan"]')?.value;
      if (sellingPlan) item.selling_plan = Number(sellingPlan);

      return item;
    }

    async addBundle(items) {
      const root = window.Shopify?.routes?.root || '/';
      const endpoint = `${root}cart/add.js`;
      const sectionIds = this.cartSectionIds().slice(0, 5);
      const sectionsUrl = `${location.pathname}${location.search}` || '/';

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections: sectionIds,
          sections_url: sectionsUrl.startsWith('/') ? sectionsUrl : `/${sectionsUrl}`
        })
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          payload.description
          || payload.message
          || 'The personalised order and gift-set add-on could not be added.'
        );
      }

      if (payload.sections) {
        this.applyCartSections(payload.sections);
        this.cartSectionsFresh = true;
      }
      return payload;
    }

    async fetchCartWithRetry(attempts = 4) {
      let lastError = null;

      for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
          if (attempt > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, 160 * attempt));
          }
          return await this.fetchCart();
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError || new Error('The cart could not be verified.');
    }

    bundleMissingItems(cart, mainItem, addOns, giftSetId) {
      const lines = cart?.items || [];
      const lineMatchesGiftSet = (line) => String(line.properties?.['_Gift set ID'] || '') === String(giftSetId);
      const missing = [];

      const mainPresent = lines.some((line) => {
        return Number(line.variant_id || line.id) === Number(mainItem.id) && lineMatchesGiftSet(line);
      });
      if (!mainPresent) missing.push(mainItem);

      addOns.forEach((addOn) => {
        const present = lines.some((line) => {
          return Number(line.variant_id || line.id) === Number(addOn.id) && lineMatchesGiftSet(line);
        });
        if (!present) missing.push(addOn);
      });

      return missing;
    }

    verifyBundleInCart(cart, mainItem, addOns, giftSetId) {
      const missing = this.bundleMissingItems(cart, mainItem, addOns, giftSetId);
      if (missing.length) {
        const addOnMissing = missing.some((item) => addOns.includes(item));
        throw new Error(addOnMissing
          ? 'The selected gift-set add-on was not confirmed in the cart.'
          : 'The personalised product was not confirmed in the cart.');
      }
      return true;
    }

    async pollForBundle(cart, mainItem, addOns, giftSetId, attempts = 3) {
      let current = cart;

      for (let attempt = 0; attempt < attempts; attempt += 1) {
        if (!this.bundleMissingItems(current, mainItem, addOns, giftSetId).length) return current;
        await new Promise((resolve) => window.setTimeout(resolve, 160 + attempt * 120));
        current = await this.fetchCartWithRetry(2);
      }

      return current;
    }

    async repairMissingBundleItems(cart, mainItem, addOns, giftSetId) {
      let current = await this.pollForBundle(cart, mainItem, addOns, giftSetId, 3);
      const missing = this.bundleMissingItems(current, mainItem, addOns, giftSetId);
      if (!missing.length) return current;

      const root = window.Shopify?.routes?.root || '/';
      const endpoint = `${root}cart/add.js`.replace(/\/{2,}/g, '/');

      for (const item of missing) {
        const isAddon = addOns.some((candidate) => Number(candidate.id) === Number(item.id));
        if (isAddon) {
          await this.addAddonLine(endpoint, item);
        } else {
          await this.addBundle([item]);
        }
      }

      current = await this.pollForBundle(await this.fetchCartWithRetry(3), mainItem, addOns, giftSetId, 3);
      this.verifyBundleInCart(current, mainItem, addOns, giftSetId);
      return current;
    }

    giftSetActualTotal(cart, giftSetId) {
      return (cart?.items || [])
        .filter((item) => String(item.properties?.['_Gift set ID'] || '') === String(giftSetId))
        .reduce((sum, item) => {
          const quantity = Math.max(1, Number(item.quantity || 1));
          const candidates = [
            Number(item.final_line_price),
            Number(item.line_price),
            Number(item.discounted_price) * quantity,
            Number(item.price) * quantity
          ];
          const value = candidates.find((candidate) => Number.isFinite(candidate) && candidate >= 0);
          return sum + (Number.isFinite(value) ? value : 0);
        }, 0);
    }

    async submitWithAddons() {
      if (this.submitting) return;

      let completedSuccessfully = false;
      let completionMessage = 'The personalised product and gift-set add-on were added to cart.';
      this.submitting = true;
      const label = this.atc?.querySelector('[data-mg-submit-label]');
      const originalLabel = label?.textContent || '';

      if (this.atc) this.atc.disabled = true;
      if (label) label.textContent = 'Adding complete order…';
      this.setError('Adding the personalised product and selected gift-set item…');

      try {
        const selectedCards = this.selectedAddonCards();
        if (!selectedCards.length) {
          throw new Error('The gift-set add-on is no longer selected. Review the gift step and try again.');
        }

        const giftSetId = this.createGiftSetId();
        const addOns = await this.addonItems(giftSetId);

        if (addOns.length !== selectedCards.length) {
          throw new Error('The selected gift-set add-on could not be prepared for the cart.');
        }

        const mainItem = this.mainProductItem(giftSetId, addOns);
        const items = [mainItem, ...addOns];

        await this.addBundle(items);

        let cart = await this.fetchCartWithRetry(5);
        cart = await this.repairMissingBundleItems(cart, mainItem, addOns, giftSetId);
        this.verifyBundleInCart(cart, mainItem, addOns, giftSetId);

        const giftSetLines = (cart.items || []).filter((item) => {
          return String(item.properties?.['_Gift set ID'] || '') === String(giftSetId);
        });

        const verifiedAddonIds = new Set(
          giftSetLines
            .filter((item) => String(item.properties?.['_Added as'] || '') === 'Product add-on')
            .map((item) => Number(item.variant_id || item.id))
        );

        const missingVerifiedAddon = addOns.find((item) => !verifiedAddonIds.has(Number(item.id)));

        if (giftSetLines.length < 1 + addOns.length || missingVerifiedAddon) {
          throw new Error('The selected gift-set add-on was not confirmed in the Shopify cart. The order was not treated as complete.');
        }

        const expectedTotal = this.displayedTotal();
        const actualGiftSetTotal = this.giftSetActualTotal(cart, giftSetId);
        if (actualGiftSetTotal > 0 && Math.abs(actualGiftSetTotal - expectedTotal) > 1) {
          this.track('pricing_mismatch', {
            expected_cents: expectedTotal,
            actual_cents: actualGiftSetTotal,
            gift_set_id: giftSetId
          });
          completionMessage = `Cart pricing needs review: expected ${this.formatMoney(expectedTotal)}, cart shows ${this.formatMoney(actualGiftSetTotal)}.`;
        }

        this.updateCartIndicators(cart);
        await this.refreshCartDrawer(cart);

        this.track('add_to_cart_success', {
          quantity: this.state.quantity,
          addons: addOns.length,
          total_cents: this.displayedTotal(),
          gift_set_id: giftSetId
        });

        this.clearSavedConfiguration(false);
        completedSuccessfully = true;
      } catch (error) {
        console.error('[Mr Golf] Bundle add error:', error);
        this.setError(error.message || 'The complete order could not be added. Please try again.');
        this.openStep(5);
        const giftStep = this.querySelector('[data-step="5"]');
        if (giftStep) {
          giftStep.classList.add('is-error');
          window.setTimeout(() => {
            giftStep.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 80);
        }
      } finally {
        this.submitting = false;
          if (this.atc) this.atc.disabled = false;
        if (label) label.textContent = originalLabel;
        this.update();
        if (completedSuccessfully) {
          this.setError(completionMessage);
        }
      }
    }

    async fetchCart() {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`.replace(/\/{2,}/g, '/'), {
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      if (!response.ok) throw new Error('The cart could not be verified.');
      return response.json();
    }

    updateCartIndicators(cart) {
      if (!cart) return;

      const count = Number(cart.item_count || 0);
      document.querySelectorAll(
        '[data-cart-count], .cart-count-bubble span, .cart-count-bubble, [aria-label*="cart" i] [data-count]'
      ).forEach((element) => {
        if (element.matches('.cart-count-bubble') && element.children.length) return;
        element.textContent = String(count);
        element.hidden = count < 1;
      });

      document.querySelectorAll('[data-cart-total], [data-cart-subtotal]').forEach((element) => {
        element.textContent = this.formatMoney(Number(cart.total_price || 0));
      });
    }

    cartSectionIds() {
      const ids = new Set(['cart-drawer', 'cart-icon-bubble']);

      const candidates = [
        document.querySelector('loess-cart-drawer, cart-drawer, [data-cart-drawer], [id*="cart-drawer" i]'),
        document.querySelector('[data-cart-count], .cart-count-bubble, [id*="cart-icon" i]')
      ].filter(Boolean);

      candidates.forEach((candidate) => {
        const section = candidate.closest?.('[id^="shopify-section-"]');
        if (section?.id) ids.add(section.id.replace(/^shopify-section-/, ''));
      });

      return [...ids].filter(Boolean);
    }

    async fetchCartSections() {
      const root = window.Shopify?.routes?.root || '/';
      const sectionIds = this.cartSectionIds();
      if (!sectionIds.length) return {};

      const separator = root.includes('?') ? '&' : '?';
      const url = `${root}${separator}sections=${encodeURIComponent(sectionIds.join(','))}`;
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) return {};
      return response.json();
    }

    applyCartSections(sections = {}) {
      Object.entries(sections || {}).forEach(([sectionId, html]) => {
        if (!html) return;

        const current = document.getElementById(`shopify-section-${sectionId}`);
        if (!current) return;

        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const next = documentFragment.getElementById(`shopify-section-${sectionId}`);
        current.innerHTML = next ? next.innerHTML : html;
      });
    }

    async refreshCartDrawer(cart = null) {
      if (!cart) {
        try {
          cart = await this.fetchCart();
        } catch (error) {
          console.warn('[Mr Golf] Cart refresh fetch failed.', error);
        }
      }

      this.updateCartIndicators(cart);

      if (!this.cartSectionsFresh) {
        try {
          const sections = await this.fetchCartSections();
          this.applyCartSections(sections);
        } catch (error) {
          console.warn('[Mr Golf] Cart section refresh failed.', error);
        }
      }
      this.cartSectionsFresh = false;

      const detail = { cart, source: 'mg-simple-personalised-product' };
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail }));

      window.setTimeout(() => {
        const drawer = document.querySelector(
          'loess-cart-drawer, cart-drawer, [data-cart-drawer], [id*="cart-drawer" i]'
        );

        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
          this.track('cart_drawer_opened');
          return;
        }

        if (drawer && typeof drawer.show === 'function') {
          drawer.show();
          this.track('cart_drawer_opened');
          return;
        }

        const trigger = document.querySelector(
          'button[aria-controls*="CartDrawer"], button[aria-controls*="cart-drawer"], [data-cart-drawer-toggle], a[href="/cart"][aria-haspopup="dialog"]'
        );
        trigger?.click();
        this.track('cart_drawer_opened');
      }, 120);
    }

    giftNoteText() {
      return String(this.querySelector('[data-note-input]')?.value || '').trim();
    }

    giftNoteSummary(limit = 90) {
      const text = this.giftNoteText();
      if (!text) return this.state.note ? 'Message required' : 'Not added';
      return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
    }

    syncGiftNoteProperties() {
      const input = this.querySelector('[data-note-input]');
      const metadata = this.querySelector('[data-note-metadata-property]');
      const text = this.state.note ? this.giftNoteText() : '';
      if (input) input.disabled = !this.state.note;
      if (metadata) metadata.value = text;
    }

    setNoteState() {
      const toggle = this.querySelector('[data-note-toggle]');
      const fields = this.querySelector('[data-note-fields]');

      toggle?.classList.toggle('is-on', this.state.note);
      toggle?.setAttribute('aria-pressed', String(this.state.note));

      if (fields) fields.hidden = !this.state.note;
      this.syncGiftNoteProperties();
      this.updateNoteCount();
    }

    updateNoteCount() {
      const input = this.querySelector('[data-note-input]');
      const count = this.querySelector('[data-note-count]');
      if (input && count) count.textContent = String(input.value.length);
    }

    baseVariantPrice() {
      return Number(this.state.variant?.price || this.variants[0]?.price || 0);
    }

    customQuantityMin() {
      const input = this.querySelector('[data-quantity-number]');
      return Math.max(2, Number(input?.min || 4));
    }

    quantityPrice(quantity) {
      const base = this.baseVariantPrice();
      const discount = quantity === 2
        ? this.twoDiscount
        : quantity >= 3
          ? this.threeDiscount
          : 0;

      return Math.round(base * quantity * (1 - discount / 100));
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(Number(cents || 0), this.moneyFormat);
      }

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency',
          currency: this.currency
        }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }

    isStepEnabled(step) {
      return this.enabledSteps.includes(Number(step));
    }

    firstEnabledStep() {
      return this.enabledSteps[0] || 0;
    }

    nextEnabledStep(step) {
      const currentIndex = this.enabledSteps.indexOf(Number(step));
      if (currentIndex < 0) {
        return this.enabledSteps.find((candidate) => candidate > Number(step)) || this.firstEnabledStep();
      }
      return this.enabledSteps[currentIndex + 1] || this.enabledSteps[currentIndex] || this.firstEnabledStep();
    }

    quantityLabel(quantity) {
      const count = Number(quantity || 1);
      if (count === 1) return `One ${this.itemSingular}`;
      return `${count} ${this.itemPlural}`;
    }

    personalisationQuantityMessage(quantity) {
      const count = Number(quantity || 1);
      const template = count > 1
        ? this.multiPersonalisationCopy
        : this.singlePersonalisationCopy;

      return template
        .replaceAll('{quantity}', String(count))
        .replaceAll('{item}', this.itemSingular)
        .replaceAll('{items}', this.itemPlural);
    }

    baseOnlyLabel() {
      return `${this.itemSingular.charAt(0).toUpperCase()}${this.itemSingular.slice(1)} only`;
    }

    openStep(step, scroll = true) {
      let target = Number(step || 0);
      if (!this.isStepEnabled(target)) {
        target = this.enabledSteps.find((candidate) => candidate >= target) || this.firstEnabledStep();
      }
      if (!target) return;

      this.state.currentStep = target;

      this.querySelectorAll('[data-step]').forEach((element) => {
        const open = Number(element.dataset.step) === this.state.currentStep;
        element.classList.toggle('is-open', open);

        const button = element.querySelector('[data-step-header]');
        button?.setAttribute('aria-expanded', String(open));
      });

      this.update();

      if (scroll && window.matchMedia('(max-width: 720px)').matches) {
        window.setTimeout(() => {
          this.querySelector(`[data-step="${this.state.currentStep}"]`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 150);
      }
    }

    stepComplete(step) {
      if (!this.isStepEnabled(step)) return true;

      switch (Number(step)) {
        case 1: return Boolean(this.state.hand);
        case 2: return Boolean(this.state.size && this.state.variant?.available);
        case 3: return Boolean(this.state.zeptoComplete);
        case 4: return Boolean(this.state.quantityTouched || this.state.currentStep > 4);
        case 5: return Boolean(this.state.giftReviewed);
        default: return true;
      }
    }

    nextBlockingStep() {
      if (this.isStepEnabled(1) && !this.state.hand) return 1;
      if (this.isStepEnabled(2) && !this.state.size) return 2;
      if (!this.state.variant?.available) return this.isStepEnabled(2) ? 2 : this.firstEnabledStep();
      return 0;
    }

    nextGuidedStep() {
      if (this.isStepEnabled(1) && !this.state.hand) return 1;
      if (this.isStepEnabled(2) && !this.state.size) return 2;
      if (this.isStepEnabled(3) && !this.state.zeptoComplete) return 3;
      if (this.isStepEnabled(4) && !this.state.quantityTouched && this.state.currentStep < 4) return 4;

      // Step 5 contains optional extras. It must never be a guided/checkout
      // requirement. Customers may use it, skip it, or purchase immediately.
      return 0;
    }

    clearStepErrors() {
      this.querySelectorAll('[data-step-error]').forEach((element) => {
        element.hidden = true;
        element.textContent = '';
      });
      this.querySelectorAll('[data-step].is-error').forEach((step) => {
        step.classList.remove('is-error');
        step.removeAttribute('aria-invalid');
      });
    }

    clearStepError(step) {
      const element = this.querySelector(`[data-step-error="${step}"]`);
      if (element) {
        element.hidden = true;
        element.textContent = '';
      }

      const stepElement = this.querySelector(`[data-step="${step}"]`);
      stepElement?.classList.remove('is-error');
      stepElement?.removeAttribute('aria-invalid');

      this.querySelectorAll(`[data-step="${step}"] [aria-invalid="true"]`).forEach((control) => {
        control.removeAttribute('aria-invalid');
      });
    }

    showStepError(step, message, focusSelector = '') {
      this.clearStepErrors();

      const stepElement = this.querySelector(`[data-step="${step}"]`);
      const element = this.querySelector(`[data-step-error="${step}"]`);

      stepElement?.classList.add('is-error');
      stepElement?.setAttribute('aria-invalid', 'true');

      if (element) {
        element.textContent = message;
        element.hidden = false;
        element.tabIndex = -1;
      }

      this.openStep(step, false);

      window.setTimeout(() => {
        stepElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });

        const target = focusSelector
          ? stepElement?.querySelector(focusSelector) || this.form?.querySelector(focusSelector)
          : null;

        target?.focus?.({ preventScroll: true });
        if (!target) element?.focus?.({ preventScroll: true });
      }, 120);
    }

    renderReceiptAddons(addons) {
      const container = this.querySelector('[data-receipt-addons]');
      if (!container) return;

      container.replaceChildren();

      addons.forEach((card) => {
        const displayPrice = Number(card.dataset.addonDisplayPriceCents || 0);
        const regularPrice = Number(card.dataset.addonRegularPriceCents || displayPrice || 0);
        const saving = Math.max(0, regularPrice - displayPrice);

        const line = document.createElement('div');
        line.className = 'mg-simple__receipt-line mg-simple__receipt-line--addon';

        const copy = document.createElement('div');
        const title = document.createElement('strong');
        const detail = document.createElement('span');
        title.textContent = card.dataset.addonTitle || 'Gift set add-on';
        detail.textContent = `${card.dataset.addonColour || ''}${card.dataset.addonPersonalisation ? ` · ${card.dataset.addonPersonalisation}` : ''}`.trim();
        copy.append(title, detail);

        const price = document.createElement('div');
        price.className = 'mg-simple__receipt-price';

        if (saving > 0) {
          const original = document.createElement('del');
          original.textContent = this.formatMoney(regularPrice);
          price.append(original);
        }

        const finalPrice = document.createElement('b');
        finalPrice.textContent = this.formatMoney(displayPrice);
        price.append(finalPrice);

        line.append(copy, price);
        container.append(line);

        if (saving > 0) {
          const adjustment = document.createElement('div');
          adjustment.className = 'mg-simple__receipt-adjustment';
          const label = document.createElement('span');
          label.textContent = 'Gift-set saving';
          const value = document.createElement('strong');
          value.textContent = `−${this.formatMoney(saving)}`;
          adjustment.append(label, value);
          container.append(adjustment);
        }
      });

      container.hidden = addons.length === 0;
    }

    update() {
      this.querySelectorAll('[data-hand-value]').forEach((button) => {
        const selected = button.dataset.handValue === this.state.hand;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });

      this.querySelectorAll('[data-size-value]').forEach((button) => {
        const selected = button.dataset.sizeValue === this.state.size;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });

      this.querySelectorAll('[data-quantity-choice]').forEach((button) => {
        const choice = Number(button.dataset.quantityChoice);
        const selected = choice === this.state.quantity;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });

      const customQuantityCard = this.querySelector('[data-custom-quantity-card]');
      const customMin = this.customQuantityMin();
      const currentPresetExists = Boolean(this.querySelector(`[data-quantity-choice="${this.state.quantity}"]`));
      customQuantityCard?.classList.toggle('is-selected', this.state.quantity >= customMin && !currentPresetExists);

      if (this.quantityInput) this.quantityInput.value = String(this.state.quantity);

      [1, 2, 3].forEach((quantity) => {
        const total = this.quantityPrice(quantity);
        const perUnit = Math.round(total / quantity);
        const fullPrice = this.baseVariantPrice() * quantity;
        const saving = Math.max(0, fullPrice - total);

        this.querySelectorAll(`[data-quantity-price="${quantity}"]`).forEach((element) => {
          element.textContent = this.formatMoney(total);
        });
        this.querySelectorAll(`[data-per-unit="${quantity}"]`).forEach((element) => {
          element.textContent = `${this.formatMoney(perUnit)} each`;
        });
        this.querySelectorAll(`[data-quantity-saving="${quantity}"]`).forEach((element) => {
          element.hidden = saving <= 0;
          element.textContent = saving > 0 ? `You save ${this.formatMoney(saving)}` : '';
        });
      });

      const extendedInput = this.querySelector('[data-quantity-number]');
      const extendedTotal = this.querySelector('[data-extended-total]');
      const customQuantityPrice = this.querySelector('[data-custom-quantity-price]');
      const customPerUnit = this.querySelector('[data-custom-per-unit]');
      const customQuantitySaving = this.querySelector('[data-custom-quantity-saving]');
      const customDisplayQuantity = this.state.quantity >= customMin && !currentPresetExists
        ? this.state.quantity
        : customMin;
      const customTotal = this.quantityPrice(customDisplayQuantity);
      const customPerUnitPrice = Math.round(customTotal / customDisplayQuantity);
      const customSaving = Math.max(0, this.baseVariantPrice() * customDisplayQuantity - customTotal);
      const quantitySaving = Math.max(0, this.baseVariantPrice() * this.state.quantity - this.quantityPrice(this.state.quantity));

      if (extendedInput && document.activeElement !== extendedInput) {
        extendedInput.value = String(customDisplayQuantity);
      }
      if (extendedTotal) {
        extendedTotal.textContent = this.state.quantity >= customMin && !currentPresetExists
          ? this.formatMoney(customTotal)
          : `From ${this.formatMoney(customTotal)}`;
      }
      if (customQuantityPrice) {
        customQuantityPrice.textContent = this.state.quantity >= customMin && !currentPresetExists
          ? `${this.state.quantity} ${this.itemPlural}`
          : `${customDisplayQuantity} ${this.itemPlural}`;
      }
      if (customPerUnit) customPerUnit.textContent = `${this.formatMoney(customPerUnitPrice)} each`;
      if (customQuantitySaving) customQuantitySaving.textContent = `You save ${this.formatMoney(customSaving)}`;

      const zeptoSummary = this.getZeptoCustomerSummary();
      const personalisationSummary = !this.isStepEnabled(3)
        ? 'Not required'
        : zeptoSummary || (this.state.zeptoComplete ? 'Personalisation complete' : 'Personalisation required');
      const addons = this.selectedAddonCards();

      const giftAction = this.querySelector('[data-gift-skip]');
      const giftActionText = giftAction?.querySelector('[data-gift-skip-text]');
      if (giftAction && giftActionText) {
        const hasSelectedExtras = addons.length > 0 || Boolean(this.state.note);
        giftAction.dataset.mode = hasSelectedExtras ? 'confirm' : 'skip';
        giftActionText.textContent = hasSelectedExtras
          ? (giftAction.dataset.confirmLabel || 'Confirm your order below')
          : (giftAction.dataset.skipLabel || 'Continue without extras');
      }

      const giftSummary = addons.length
        ? addons.map((card) => `${card.dataset.addonTitle || 'Gift set add-on'} · ${card.dataset.addonColour || ''}`.trim()).join(', ')
        : this.baseOnlyLabel();

      const summaries = {
        1: this.state.hand || 'Not selected',
        2: this.state.size || 'Not selected',
        3: personalisationSummary,
        4: this.quantityLabel(this.state.quantity),
        5: this.state.note
          ? `${giftSummary} + note`
          : giftSummary
      };

      const samePersonalisationText = this.querySelector('[data-same-personalisation-text]');
      if (samePersonalisationText) {
        samePersonalisationText.textContent = this.personalisationQuantityMessage(this.state.quantity);
      }

      const confirmation = this.querySelector('[data-personalisation-confirmation]');
      const confirmationValue = this.querySelector('[data-personalisation-confirmation-value]');
      if (confirmation) confirmation.classList.toggle('is-complete', Boolean(this.state.zeptoComplete));
      if (confirmationValue) confirmationValue.textContent = personalisationSummary;

      Object.entries(summaries).forEach(([step, value]) => {
        this.querySelectorAll(`[data-step-summary="${step}"]`).forEach((element) => {
          element.textContent = value;
        });
      });

      this.querySelectorAll('[data-step]').forEach((element) => {
        const step = Number(element.dataset.step);
        element.classList.toggle('is-complete', this.stepComplete(step));
      });

      const progress = this.querySelector('[data-progress]');
      const currentProgressIndex = Math.max(0, this.enabledSteps.indexOf(this.state.currentStep));
      progress?.setAttribute('aria-valuemax', String(this.enabledSteps.length));
      progress?.setAttribute('aria-valuenow', String(currentProgressIndex + 1));
      progress?.setAttribute('aria-valuetext', `Step ${currentProgressIndex + 1} of ${this.enabledSteps.length}`);

      this.querySelectorAll('[data-progress-segment]').forEach((segment) => {
        const step = Number(segment.dataset.progressSegment);
        const visualIndex = this.enabledSteps.indexOf(step);
        const current = step === this.state.currentStep;
        const complete = this.stepComplete(step) || (visualIndex >= 0 && visualIndex < currentProgressIndex);
        const state = current ? 'current' : complete ? 'complete' : 'upcoming';

        segment.dataset.state = state;
        segment.classList.toggle('is-complete', state === 'complete');
        segment.classList.toggle('is-current', state === 'current');
        segment.title = state === 'current'
          ? `Current step: ${visualIndex + 1}`
          : state === 'complete'
            ? `Completed step: ${visualIndex + 1}`
            : `Upcoming step: ${visualIndex + 1}`;
      });

      this.setNoteState();

      const variantReady = Boolean(this.state.variant?.available);
      const purchaseReady = variantReady
        && (!this.isStepEnabled(1) || Boolean(this.state.hand))
        && (!this.isStepEnabled(2) || Boolean(this.state.size));
      const personalisationDetected = !this.isStepEnabled(3) || Boolean(this.state.zeptoComplete);
      const personalisationContinue = this.querySelector('[data-personalisation-continue]');
      const personalisationReady = this.querySelector('[data-personalisation-ready]');
      if (personalisationContinue) {
        personalisationContinue.disabled = !personalisationDetected;
        personalisationContinue.classList.toggle('is-ready', personalisationDetected);
      }
      if (personalisationReady) personalisationReady.hidden = !personalisationDetected;
      const guidedStep = this.nextGuidedStep();
      const flowReady = purchaseReady && !guidedStep;

      const blockingStep = this.nextBlockingStep();
      const statusMessage = blockingStep === 1
        ? this.copy.statusHand
        : blockingStep === 2
          ? this.copy.statusSize
          : !variantReady
            ? this.copy.statusUnavailable
            : !personalisationDetected
              ? this.copy.statusPersonalisation
              : !this.state.quantityTouched && this.isStepEnabled(4) && this.state.currentStep < 4
                ? this.copy.statusQuantity
                : this.copy.statusReview;

      if (this.status) this.status.textContent = statusMessage;
      const guidance = this.querySelector('[data-atc-guidance]');
      if (guidance) {
        guidance.textContent = statusMessage;
        guidance.classList.toggle('is-warning', Boolean(blockingStep || !personalisationDetected));
      }

      this.querySelectorAll('[data-ready-state]').forEach((element) => {
        element.textContent = flowReady ? this.copy.ready : this.copy.continue;
        element.classList.toggle('is-ready', Boolean(flowReady));
      });

      this.querySelectorAll('[data-review-hand], [data-proof-hand]').forEach((element) => {
        element.textContent = this.state.hand || '—';
      });
      this.querySelectorAll('[data-review-size], [data-proof-size]').forEach((element) => {
        element.textContent = this.state.size || '—';
      });
      this.querySelectorAll('[data-review-personalisation], [data-summary-personalisation], [data-proof-dialog-personalisation]').forEach((element) => {
        element.textContent = personalisationSummary;
      });
      this.querySelectorAll('[data-proof-quantity]').forEach((element) => {
        element.textContent = summaries[4];
      });
      this.querySelectorAll('[data-review-gift], [data-proof-gift]').forEach((element) => {
        element.textContent = giftSummary;
      });
      this.querySelectorAll('[data-review-note], [data-proof-note]').forEach((element) => {
        element.textContent = this.state.note ? this.giftNoteSummary() : 'Not added';
      });

      const basePrice = this.quantityPrice(this.state.quantity);
      const fullProductPrice = this.baseVariantPrice() * this.state.quantity;
      const addonPrice = this.addonDisplayTotal();
      const addonRegular = this.addonRegularTotal();
      const addonSaving = this.addonSavingTotal();
      const displayedTotal = basePrice + addonPrice;

      this.querySelectorAll('[data-product-price], [data-mobile-context-price]').forEach((element) => {
        element.textContent = this.formatMoney(this.baseVariantPrice());
      });

      const receiptProductTitle = this.querySelector('[data-receipt-product-title]');
      const receiptProductOriginal = this.querySelector('[data-receipt-product-original]');
      const receiptProductPrice = this.querySelector('[data-receipt-product-price]');
      if (receiptProductTitle) receiptProductTitle.textContent = `${this.quantityLabel(this.state.quantity)} personalised`;
      if (receiptProductOriginal) {
        receiptProductOriginal.hidden = quantitySaving <= 0;
        receiptProductOriginal.textContent = quantitySaving > 0 ? this.formatMoney(fullProductPrice) : '';
      }
      if (receiptProductPrice) receiptProductPrice.textContent = this.formatMoney(basePrice);

      const receiptMultibuy = this.querySelector('[data-receipt-multibuy]');
      const receiptMultibuyValue = this.querySelector('[data-receipt-multibuy-value]');
      if (receiptMultibuy) receiptMultibuy.hidden = quantitySaving <= 0;
      if (receiptMultibuyValue) receiptMultibuyValue.textContent = quantitySaving > 0 ? `−${this.formatMoney(quantitySaving)}` : '';

      this.renderReceiptAddons(addons);

      const receiptNote = this.querySelector('[data-receipt-note]');
      const hasGiftNote = Boolean(this.state.note && this.giftNoteText());
      if (receiptNote) receiptNote.hidden = !hasGiftNote;
      const regularOrderTotal = fullProductPrice + addonRegular;
      const totalSaving = Math.max(0, regularOrderTotal - displayedTotal);
      const orderTotal = this.querySelector('[data-order-total]');
      const orderOriginal = this.querySelector('[data-order-original]');
      const orderSaving = this.querySelector('[data-order-saving]');

      if (orderTotal) orderTotal.textContent = this.formatMoney(displayedTotal);
      if (orderOriginal) {
        orderOriginal.hidden = totalSaving <= 0;
        orderOriginal.textContent = totalSaving > 0 ? this.formatMoney(regularOrderTotal) : '';
      }
      if (orderSaving) {
        orderSaving.hidden = totalSaving <= 0;
        orderSaving.textContent = totalSaving > 0 ? `${this.dataset.savingPrefix || 'You save'} ${this.formatMoney(totalSaving)}` : '';
      }

      this.querySelectorAll('[data-confirm-total]').forEach((element) => element.textContent = this.formatMoney(displayedTotal));
      this.querySelectorAll('[data-confirm-hand]').forEach((element) => element.textContent = this.state.hand || '—');
      this.querySelectorAll('[data-confirm-size]').forEach((element) => element.textContent = this.state.size || '—');
      this.querySelectorAll('[data-confirm-personalisation]').forEach((element) => element.textContent = personalisationSummary);
      this.querySelectorAll('[data-confirm-quantity]').forEach((element) => element.textContent = summaries[4]);
      this.querySelectorAll('[data-confirm-gift]').forEach((element) => element.textContent = giftSummary);
      const hasGiftNoteForConfirmation = Boolean(this.state.note && this.giftNoteText());
      this.querySelectorAll('[data-confirm-note-row]').forEach((row) => row.hidden = !hasGiftNoteForConfirmation);
      this.querySelectorAll('[data-confirm-note]').forEach((element) => {
        element.textContent = hasGiftNoteForConfirmation ? this.giftNoteSummary(140) : '';
      });
      this.querySelectorAll('[data-confirm-same-personalisation]').forEach((element) => {
        element.textContent = this.state.quantity > 1
          ? `All ${this.state.quantity} ${this.itemPlural} will use the same personalisation.`
          : `The personalisation applies to this ${this.itemSingular}.`;
      });
      this.updateCartMetadata();

      this.querySelectorAll('[data-simple-addon-card]').forEach((card) => {
        const value = card.querySelector('[data-addon-personalisation-value]');
        const actualPersonalisation = zeptoSummary || (
          personalisationDetected
            ? personalisationSummary
            : 'Complete the personalisation above'
        );

        card.dataset.addonPersonalisation = zeptoSummary || (personalisationDetected ? personalisationSummary : '');
        if (value) value.textContent = actualPersonalisation;
      });

      this.querySelectorAll('[data-atc-total], [data-mobile-total]').forEach((element) => {
        element.textContent = this.formatMoney(displayedTotal);
      });

      this.querySelectorAll('[data-atc-original]').forEach((element) => {
        element.hidden = totalSaving <= 0;
        element.textContent = totalSaving > 0 ? this.formatMoney(regularOrderTotal) : '';
      });

      this.querySelectorAll('[data-atc-saving]').forEach((element) => {
        element.hidden = totalSaving <= 0;
        element.textContent = totalSaving > 0 ? `${this.dataset.savingPrefix || 'You save'} ${this.formatMoney(totalSaving)}` : '';
      });

      this.querySelectorAll('[data-mobile-summary]').forEach((element) => {
        element.textContent = this.isStepEnabled(4) ? summaries[4] : this.itemSingular;
      });

      this.querySelectorAll('[data-mobile-action]').forEach((button) => {
        const canPurchase = purchaseReady && !guidedStep;
        button.classList.toggle('is-purchase-ready', canPurchase);
        button.textContent = canPurchase ? `${this.copy.addToCart} · ${this.formatMoney(displayedTotal)}` : this.copy.continue;
      });

      if (this.atc) {
        const knownUnavailable = Boolean(this.state.variant && !this.state.variant.available);
        this.atc.disabled = Boolean(this.submitting || knownUnavailable);
        const label = this.atc.querySelector('[data-mg-submit-label]');
        if (label) {
          label.textContent = blockingStep === 1
            ? 'Choose the product option to continue'
            : blockingStep === 2
              ? 'Choose the size to continue'
              : this.copy.addToCart;
        }
      }
      this.scheduleSaveConfiguration();
    }

    validateBeforeSubmit() {
      this.clearStepErrors();
      this.checkZepto();

      if (this.isStepEnabled(1) && !this.state.hand) {
        this.showStepError(1, 'Please select the glove hand before continuing.', '[data-hand-value]');
        this.track('validation_error', { field: 'hand' });
        this.setError('Choose the glove hand.');
        return false;
      }

      if (this.isStepEnabled(2) && !this.state.size) {
        this.showStepError(2, 'Please select an available size before continuing.', '[data-size-value]:not(:disabled)');
        this.track('validation_error', { field: 'size' });
        this.setError('Choose an available size.');
        return false;
      }

      if (!this.state.variant?.available) {
        const step = this.isStepEnabled(2) ? 2 : this.firstEnabledStep();
        this.showStepError(step, 'The selected product option is currently unavailable.');
        this.setError('The selected product option is unavailable.');
        return false;
      }

      if (this.isStepEnabled(3) && !this.state.zeptoComplete) {
        this.showStepError(
          3,
          this.dataset.personalisationContinueError || 'Enter the personalisation text and choose a colour before continuing.',
          'input:not([type="hidden"]), textarea, select, [role="button"]'
        );
        this.track('validation_error', { field: 'personalisation' });
        this.setError('Complete the personalisation.');
        return false;
      }

      if (this.isStepEnabled(5) && this.state.note && !this.giftNoteText()) {
        this.showStepError(5, 'Enter the complimentary gift-note message before continuing.', '[data-note-input]');
        this.track('validation_error', { field: 'gift_note' });
        this.setError('Enter the gift-note message.');
        return false;
      }

      if (typeof this.form?.reportValidity === 'function' && !this.form.reportValidity()) {
        const invalid = this.form.querySelector(':invalid');
        const step = invalid?.closest?.('[data-step]')?.dataset?.step;
        if (invalid && (this.zeptoContainer?.contains(invalid) || Number(step) === 3)) {
          this.showStepError(3, 'Please complete all required personalisation options.', ':invalid');
          invalid?.setAttribute('aria-invalid', 'true');
          this.track('validation_error', { field: 'personalisation' });
          this.setError('Complete the required personalisation.');
        } else if (step) {
          this.showStepError(Number(step), 'Please complete this required option.');
        }
        return false;
      }

      return true;
    }

    setError(message) {
      if (this.status) this.status.textContent = message;
    }

    overlayFocusable(overlay) {
      if (!overlay) return [];
      return [...overlay.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => visible(element));
    }

    lockPageForOverlay(overlay) {
      this.activeOverlay = overlay;

      // The product-image zoom overlay is nested inside the gallery <aside>.
      // The previous implementation made every top-level component child
      // inert. That included the <aside> containing the overlay itself, which
      // makes every descendant — including the mobile close button — inert.
      //
      // Instead, preserve the complete ancestor path from the overlay back to
      // this component and inert only SIBLINGS along that path. This keeps the
      // modal interactive while the rest of the product builder is correctly
      // unavailable behind it.
      const inertTargets = [];
      const seen = new Set();
      let current = overlay;

      while (current && current !== this) {
        const parent = current.parentElement;
        if (!parent) break;

        [...parent.children].forEach((sibling) => {
          if (sibling === current) return;
          if (sibling.matches?.('script, style, link')) return;
          if (seen.has(sibling)) return;

          seen.add(sibling);
          inertTargets.push(sibling);
        });

        current = parent;
      }

      this.overlayInertTargets = inertTargets;

      this.overlayInertTargets.forEach((element) => {
        if (!element.inert) {
          element.dataset.mgAddedInert = 'true';
          element.inert = true;
        }
      });

      const body = document.body;
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      this.bodyLockState = {
        scrollY,
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overflow: body.style.overflow
      };

      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      document.documentElement.classList.add('mg-modal-open');
      body.classList.add('mg-modal-open');
    }

    unlockPageAfterOverlay() {
      this.overlayInertTargets.forEach((element) => {
        if (element.dataset.mgAddedInert === 'true') {
          element.inert = false;
          delete element.dataset.mgAddedInert;
        }
      });
      this.overlayInertTargets = [];

      const body = document.body;
      const state = this.bodyLockState;
      if (state) {
        body.style.position = state.position;
        body.style.top = state.top;
        body.style.left = state.left;
        body.style.right = state.right;
        body.style.width = state.width;
        body.style.overflow = state.overflow;
        window.scrollTo(0, state.scrollY || 0);
      }

      this.bodyLockState = null;
      this.activeOverlay = null;
      document.documentElement.classList.remove('mg-modal-open');
      body.classList.remove('mg-modal-open');
    }

    closeOverlay(overlay) {
      if (!overlay) return;
      overlay.hidden = true;
      overlay.setAttribute('aria-hidden', 'true');
      this.unlockPageAfterOverlay();
      this.lastDialogTrigger?.focus?.({ preventScroll: true });
    }

    openDialog(selector) {
      const dialog = this.querySelector(selector) || document.querySelector(selector);
      if (!dialog) {
        console.warn('[Mr Golf] Dialog target was not found:', selector);
        return;
      }

      this.lastDialogTrigger = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

      if (dialog.matches?.('[data-modal-overlay]')) {
        dialog.hidden = false;
        dialog.setAttribute('aria-hidden', 'false');
        this.lockPageForOverlay(dialog);
        window.requestAnimationFrame(() => {
          const focusable = this.overlayFocusable(dialog);
          (focusable[0] || dialog.querySelector('[role="dialog"]'))?.focus?.({ preventScroll: true });
        });
        return;
      }

      try {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      } catch (error) {
        console.warn('[Mr Golf] Native dialog failed; using open attribute.', error);
        dialog.setAttribute('open', '');
      }
    }

    disconnectedCallback() {
      this.zeptoObserver?.disconnect();
      this.stampedObserver?.disconnect();
      this.stampedBadgeObserver?.disconnect();
      this.mobileDockObserver?.disconnect();
      this.unbindZeptoCapture();

      if (this.form && this.formSubmitHandler) {
        this.form.removeEventListener('submit', this.formSubmitHandler, true);
        this.formSubmitHandler = null;
      }

      if (this.shopifySectionLoadHandler) {
        document.removeEventListener('shopify:section:load', this.shopifySectionLoadHandler);
        this.shopifySectionLoadHandler = null;
      }

      if (this.galleryMediaQuery && this.galleryMediaQueryHandler) {
        this.galleryMediaQuery.removeEventListener?.('change', this.galleryMediaQueryHandler);
      }

      const track = this.querySelector('[data-gallery-main]');
      if (track && this.galleryScrollHandler) {
        track.removeEventListener('scroll', this.galleryScrollHandler);
      }

      if (this.activeOverlay) this.unlockPageAfterOverlay();

      [
        this.zeptoDebounce,
        this.saveTimer,
        this.galleryScrollTimer,
        ...this.restoreTimers,
        ...this.zeptoInitTimers
      ].forEach((timer) => window.clearTimeout(timer));

      this.restoreTimers = [];
      this.zeptoInitTimers = [];
    }
  }

  if (!customElements.get(SELECTOR)) {
    customElements.define(SELECTOR, MGSimplePersonalisedProduct);
  }
})();
